// src/admin/AdminPanel.js
import React, { useEffect, useMemo, useState } from "react";

import {
  adminLogin,
  adminFetchParks,
  adminListAnnouncements,
  adminCreateAnnouncement,
  adminUpdateAnnouncement,
  adminDeleteAnnouncement,
  adminListReferrals,
  adminUpdateReferral,
  adminListWithdrawals,
  adminDownloadWithdrawalsExcel,
  adminUpdateWithdrawal,
  adminGetRewards,
  adminSetRewards,
  adminListPaymentMandates,
  adminDownloadMandatesExcel,
  adminUploadPaymentMandates,
  adminPaymentMandateUploadProgress,
  adminPaymentMandateUploadControl,
  adminClearPaymentMandates,
  adminDownloadMandateTemplate,
  adminUpdatePaymentMandate,
  adminDeletePaymentMandate,
  adminLookupMandateDriver,
  adminAddPaymentMandate,
  adminDownloadMandateFailedExcel,
  adminGetPayoutMaintenance,
  adminGetPayoutBalances,
  adminSetPayoutMaintenance,
  adminSetVelixMaintenance,
} from "../Api";
import MonthDayPicker from "../components/MonthDayPicker";
import AdminVakifbankTyPaymentsPage from "./AdminVakifbankTyPaymentsPage";

const LOGO_SRC = `${process.env.PUBLIC_URL || ""}/logo_sitaxi.png?v=20260915a`;

function h(tag, props, ...children) {
  return React.createElement(tag, props, ...children);
}

function safeDateLabel(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("tr-TR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function formatMoneyTry(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  return `${Number(amount).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}₺`;
}

function outflowRangeCaption(preset) {
  if (preset === "today") return "Bugün hesaptan çıkan";
  if (preset === "month") return "Bu ay hesaptan çıkan";
  if (preset === "all") return "Tüm tarihlerde hesaptan çıkan";
  return "Seçili aralıkta hesaptan çıkan";
}

function mandateScheduleStatusLabel(it) {
  if (!it) return "-";
  if (it.driver_disabled) return "Sürücü kapattı";
  if (it.enabled) return "Aktif";
  return "Pasif";
}

function mandateSourceLabel(it) {
  const s = String(it?.source || "");
  if (s === "admin_mandate") return it.custom_slots ? "Admin" : "Excel";
  return "Sürücü";
}

function phoneDigitsLast10(v) {
  const d = String(v || "").replace(/\D/g, "");
  if (d.length >= 11 && d.startsWith("90")) return d.slice(-10);
  if (d.length === 11 && d.startsWith("0")) return d.slice(-10);
  return d.length > 10 ? d.slice(-10) : d;
}

/** Telefon filtresi: son 10 haneye göre (kısmi aramada en az 7 hane, includes ile herkesi yakalamasın). */
function phoneFilterMatches(phone, query) {
  const q = String(query || "").replace(/\D/g, "");
  if (!q) return true;
  const p = phoneDigitsLast10(phone);
  const q10 = phoneDigitsLast10(q);
  if (!p) return false;
  if (q10.length >= 10) return p === q10;
  if (q.length >= 7) return p.endsWith(q) || p.includes(q10 || q);
  // Çok kısa sorgu (905 gibi) tüm Türkiye numaralarını açmasın
  return false;
}

function dedupeWithdrawalsById(list) {
  const arr = Array.isArray(list) ? list : [];
  const seen = new Set();
  const out = [];
  for (const it of arr) {
    const id = String(it?.id || "");
    if (id) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    out.push(it);
  }
  return out;
}

function hhmm(h, m) {
  return `${String(Number(h) || 0).padStart(2, "0")}:${String(Number(m) || 0).padStart(2, "0")}`;
}

function parseHhmm(v) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(v || "").trim());
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

function mandateHoursLabel(it) {
  const h1 = hhmm(it?.morning_hour ?? 9, it?.morning_minute ?? 0);
  if (it?.second_slot_enabled) {
    return `${h1}+${hhmm(it?.evening_hour ?? 21, it?.evening_minute ?? 0)}`;
  }
  return h1;
}

const MANDATE_WEEKDAYS = [
  { value: 0, label: "Pazartesi" },
  { value: 1, label: "Salı" },
  { value: 2, label: "Çarşamba" },
  { value: 3, label: "Perşembe" },
  { value: 4, label: "Cuma" },
  { value: 5, label: "Cumartesi" },
  { value: 6, label: "Pazar" },
];
function mandateFrequencyLabel(it) {
  const freq = String(it?.frequency || "daily").toLowerCase();
  if (freq === "every_2_days") return "2 günde bir";
  if (freq === "every_3_days") return "3 günde bir";
  if (freq === "weekly") {
    const day = MANDATE_WEEKDAYS.find((d) => d.value === Number(it?.weekday))?.label || "gün seçilmedi";
    return `Haftalık · ${day}`;
  }
  if (freq === "monthly") {
    const day = Number(it?.day_of_month) || 0;
    return day ? `Aylık · ayın ${day}. günü` : "Aylık";
  }
  return "Günlük";
}

function mandateWalletDisplay(it) {
  const stored = String(it?.wallet_number_masked || "").trim();
  if (stored && stored !== "-") return stored;
  const looked = String(it?.wallet_lookup_masked || "").trim();
  if (looked && looked !== "-") return looked;
  return "";
}

function mandateWalletFillHint(it) {
  if (!it) return "";
  if (it.wallet_lookup_source === "sipay_iban" && !String(it.wallet_number || "").trim()) {
    return "Cüzdan no Sipay IBAN kaydından alındı. Bu telefonda kişisel cüzdan sorgusu boş döndü; FAST hatası da Sipay IBAN yüzünden. Kaydet ile Walletgate KYC 40 doğrulanır.";
  }
  if (!mandateWalletDisplay(it)) {
    return "Sipay bu telefonda kişisel cüzdan bulamadı.";
  }
  return "";
}

function mandateIbanDisplay(it) {
  const stored = String(it?.iban_masked || "").trim();
  if (stored && stored !== "-") return stored;
  if (String(it?.iban || "").trim()) return it.iban;
  const looked = String(it?.iban_lookup_masked || "").trim();
  if (looked && looked !== "-") return looked;
  return "-";
}

function mandateIbanFillHint(it) {
  if (!it) return "";
  if (it.iban_lookup_conflict) {
    return "Bu telefonda birden fazla IBAN var. Otomatik doldurulmadı — yanlış ödeme riski.";
  }
  if (it.iban_lookup) {
    const src = it.iban_lookup_source === "excel" ? "Excel yüklemesi" : "önceki başarılı ödeme";
    return `IBAN ${src} kaydından otomatik dolduruldu. Kaydetmeden önce kontrol edin.`;
  }
  if (!String(it.iban || "").trim()) {
    return "Bu telefona ait kayıtlı IBAN bulunamadı (Excel ve önceki ödemelerde yok).";
  }
  return "";
}

function filledMandateIban(it) {
  const stored = String(it?.iban || "").trim();
  if (stored) return stored;
  if (it?.iban_lookup_conflict) return "";
  return String(it?.iban_lookup || "").trim();
}

function renderMandateMethodToggle(value, onChange, disabled) {
  return h(
    "div",
    { className: "admin-method-toggle" },
    h(
      "button",
      {
        type: "button",
        className: "admin-method-btn" + (value === "iban" ? " active" : ""),
        disabled,
        onClick: () => onChange("iban"),
      },
      "IBAN"
    ),
    h(
      "button",
      {
        type: "button",
        className: "admin-method-btn" + (value === "wallet" ? " active" : ""),
        disabled,
        onClick: () => onChange("wallet"),
      },
      "Sipay Cüzdan"
    )
  );
}

function renderMandateHourFields({ hour1, setHour1, hour2On, setHour2On, hour2, setHour2, disabled }) {
  return h(
    "div",
    { className: "admin-hour-fields" },
    h("label", { className: "label" }, "Ödeme saati"),
    h("input", {
      className: "input",
      type: "time",
      value: hour1,
      disabled,
      onChange: (e) => setHour1(e.target.value),
    }),
    hour2On
      ? h(
          "div",
          { className: "admin-hour-second" },
          h("label", { className: "label" }, "2. saat"),
          h("input", {
            className: "input",
            type: "time",
            value: hour2,
            disabled,
            onChange: (e) => setHour2(e.target.value),
          }),
          h(
            "button",
            {
              type: "button",
              className: "btn-outline",
              disabled,
              onClick: () => {
                setHour2On(false);
                setHour2("");
              },
            },
            "Saati Kaldır"
          )
        )
      : h(
          "button",
          {
            type: "button",
            className: "btn-outline admin-hour-add-btn",
            disabled,
            onClick: () => setHour2On(true),
          },
          "Saat Ekle"
        )
  );
}

function hoursPayloadFromInputs(hour1, hour2On, hour2) {
  const t1 = parseHhmm(hour1);
  if (!t1) return { error: "Ödeme saati seçin." };
  const payload = {
    morning_hour: t1.hour,
    morning_minute: t1.minute,
    second_slot_enabled: false,
  };
  if (hour2On) {
    const t2 = parseHhmm(hour2);
    if (!t2) return { error: "2. saat seçin." };
    if (t1.hour === t2.hour && t1.minute === t2.minute) {
      return { error: "İki saat farklı olmalı." };
    }
    payload.second_slot_enabled = true;
    payload.evening_hour = t2.hour;
    payload.evening_minute = t2.minute;
  }
  return { payload };
}

function renderMandatePeriodFields({
  frequency,
  setFrequency,
  weekday,
  setWeekday,
  dayOfMonth,
  setDayOfMonth,
  disabled,
}) {
  return h(
    "div",
    { className: "auto-schedule-row auto-schedule-row-period", style: { marginTop: 12, maxWidth: 520 } },
    h(
      "div",
      { className: "auto-schedule-field" },
      h("label", { className: "label" }, "Periyot"),
      h(
        "select",
        {
          className: "input",
          value: frequency,
          disabled,
          onChange: (e) => setFrequency(e.target.value),
        },
        h("option", { value: "daily" }, "Günlük"),
        h("option", { value: "every_2_days" }, "2 günde bir"),
        h("option", { value: "every_3_days" }, "3 günde bir"),
        h("option", { value: "weekly" }, "Haftalık"),
        h("option", { value: "monthly" }, "Aylık")
      )
    ),
    h(
      "div",
      { className: "auto-schedule-field" },
      h("label", { className: "label" }, frequency === "monthly" ? "Ayın günü" : "Gün"),
      frequency === "weekly"
        ? h(
            "select",
            {
              className: "input",
              value: weekday,
              disabled,
              onChange: (e) => setWeekday(Number(e.target.value)),
            },
            MANDATE_WEEKDAYS.map((d) => h("option", { key: d.value, value: d.value }, d.label))
          )
        : frequency === "monthly"
          ? h(MonthDayPicker, {
              pickerId: "admin-mandate-day",
              value: dayOfMonth,
              disabled,
              onChange: setDayOfMonth,
            })
          : h(
              "select",
              { className: "input", value: "every", disabled: true },
              h(
                "option",
                { value: "every" },
                frequency === "every_2_days"
                  ? "2 günde bir"
                  : frequency === "every_3_days"
                    ? "3 günde bir"
                    : "Her gün"
              )
            )
    ),
    frequency === "monthly"
      ? h(
          "div",
          { className: "month-day-hint" },
          "Seçilen gün o ay yoksa ödeme bir önceki güne alınır."
        )
      : null
  );
}

function normalizePhoneInput(v) {
  return String(v || "").replace(/[^\d+]/g, "");
}

function mandateMoneySent(it) {
  const ps = String(it?.pay_status || "").toLowerCase();
  return ["paid", "completed", "success"].includes(ps);
}

function mandateUploadStatusLabel(st) {
  const s = String(st || "").toLowerCase();
  if (s === "api_waiting") return "İşyerimPOS yanıtı bekleniyor…";
  if (s === "processing") return "Hazırlanıyor…";
  if (s === "ok" || s === "paid") return "Başarılı";
  if (s === "failed") return "Başarısız";
  if (s === "paused") return "Duraklatıldı";
  if (s === "cancelled") return "İptal";
  if (s === "done") return "Bitti";
  if (s === "starting") return "Başlıyor…";
  return st || "…";
}

function payoutMethodLabel(method) {
  const m = String(method || "iban").toLowerCase();
  if (m === "wallet") return "Sipay Cüzdan";
  return "IBAN";
}

function isWalletPayout(it) {
  return String(it?.payout_method || "iban").toLowerCase() === "wallet";
}

function isVakifbankAccountPayout(it) {
  if (isWalletPayout(it)) return false;
  const provider = String(it?.payout_provider || "").toLowerCase();
  if (provider === "vakifbank") return true;
  if (provider) return false;
  return Boolean(it?.vakifbank_kurum_islem_id || it?.vakifbank_raw);
}

function withdrawalNetSent(it) {
  const direct = Number(it?.payout_amount ?? it?.net_amount);
  if (Number.isFinite(direct)) return direct;
  return Math.max(0, (Number(it?.amount) || 0) - (Number(it?.fee) || 0));
}

function vakifbankLaterFailed(it) {
  if (it?.yandex_refunded || it?.yandex_refund_tx_id) return true;
  const islem = String(it?.vakifbank_last_islem_durumu || "");
  const q = String(it?.vakifbank_last_query_status || "").toUpperCase();
  if (islem === "IadeEdildi" || islem === "Hatali" || islem === "IptalEdildi") return true;
  if (q === "FAILED" || q === "CANCELLED" || q === "REFUNDED") return true;
  return false;
}

function isWithdrawalFailed(it) {
  if (vakifbankLaterFailed(it)) return true;
  const s = String(it?.status || "").toLowerCase();
  const sipay = String(it?.sipay_status || "").toUpperCase();
  if (
    s === "payout_failed" ||
    s === "deduct_without_payout" ||
    s === "refunded" ||
    s === "failed" ||
    s === "rejected"
  ) {
    return true;
  }
  if (sipay === "FAILED" || sipay === "REJECTED") return true;
  return false;
}

function isWithdrawalDefinitelyPaid(it) {
  if (isWithdrawalFailed(it)) return false;
  const s = String(it?.status || "").toLowerCase();
  if (s !== "paid") return false;
  const provider = String(it?.payout_provider || "").toLowerCase();
  if (provider === "vakifbank" && it?.vakifbank_query_confirmed === false) return false;
  return true;
}

function isWithdrawalBankPending(it) {
  if (!it || isWithdrawalDefinitelyPaid(it) || isWithdrawalFailed(it)) return false;
  const s = String(it?.status || "").toLowerCase();
  return s === "payout_pending" || s === "payout_sending" || s === "awaiting_payout";
}

function isWithdrawalSending(it) {
  if (!it || isWithdrawalFailed(it) || isWithdrawalDefinitelyPaid(it)) return false;
  const s = String(it?.status || "").toLowerCase();
  return s === "awaiting_payout" || s === "processing" || s === "approved";
}

function isWithdrawalSentOk(it) {
  return isWithdrawalDefinitelyPaid(it);
}

function yandexStatusLine(it) {
  const s = String(it?.status || "").toLowerCase();
  const sipay = String(it?.sipay_status || "").toUpperCase();
  if (s === "paid" || sipay === "COMPLETED") return "";
  if (s === "payout_pending" || s === "payout_sending") return "";
  if (s === "refunded") return "";
  const unsettled = it?.yandex_unsettled === true || it?.yandex_deduct_confirmed === false || s === "yandex_unconfirmed";
  const st = String(it?.yandex_status || it?.yandex_raw?.status || "").trim();
  const desc = String(it?.yandex_status_description || it?.yandex_raw?.status_description || "").trim();
  if (!unsettled && s !== "processing" && s !== "awaiting_payout") return "";
  if (!st && !desc) return "";
  if (st && desc) return `Yandex: ${st} — ${desc}`;
  return `Yandex: ${st || desc}`;
}

function withdrawalFailureReason(it) {
  const s = String(it?.status || "").toLowerCase();
  if (s === "deduct_without_payout" || it?.failure_reason === "deduct_without_payout") {
    return String(it?.yandex_refund_error || "Düşüm yapıldı, gönderim yapılmadı.").trim();
  }
  const sipayErr = String(it?.sipay_error || "").trim();
  if (sipayErr) return sipayErr;
  const yandexErr = String(it?.yandex_error || "").trim();
  if (yandexErr && String(it?.status || "").toLowerCase() !== "paid") return yandexErr;
  const yandexLine = yandexStatusLine(it);
  if (yandexLine && String(it?.status || "").toLowerCase() !== "paid") return yandexLine;
  const note = String(it?.note || "").trim();
  if (note) return note;
  if (s === "yandex_unconfirmed") return "Yandex düşümü onaylanmadı (in_progress).";
  if (s === "refunded") return "Sipay başarısız, Yandex bakiyesi iade edildi.";
  if (s === "rejected") return "Talep reddedildi.";
  if (s === "failed" || s === "payout_failed") return "Gönderim başarısız.";
  return "Bilinmeyen hata";
}

function foldTr(value) {
  return String(value || "")
    .replace(/İ/g, "i")
    .replace(/I/g, "i")
    .replace(/ı/g, "i")
    .replace(/Ş/g, "s")
    .replace(/ş/g, "s")
    .replace(/Ğ/g, "g")
    .replace(/ğ/g, "g")
    .replace(/Ü/g, "u")
    .replace(/ü/g, "u")
    .replace(/Ö/g, "o")
    .replace(/ö/g, "o")
    .replace(/Ç/g, "c")
    .replace(/ç/g, "c")
    .toLowerCase();
}

function isGenericBankStatus(msg) {
  const norm = foldTr(msg).replace(/\s+/g, " ").trim();
  return [
    "iade edildi",
    "iadeedildi",
    "tahsil edildi",
    "basarili",
    "odeme basarili",
  ].includes(norm);
}

function sentRecipientName(it) {
  const items = it?.vakifbank_raw?.items;
  const row = Array.isArray(items) ? items[0] : null;
  return String(row?.AliciAdi || it?.account_holder || "").trim();
}

function nameDoubled(name) {
  const s = String(name || "").trim();
  if (s.length < 8 || s.length % 2 !== 0) return false;
  const half = s.length / 2;
  return foldTr(s.slice(0, half)) === foldTr(s.slice(half));
}

function bankRefundReason(it) {
  const code = String(it?.sipay_provider_code || "").trim();
  const msg = String(it?.sipay_provider_message || it?.sipay_error || "").trim();
  const known = {
    "02": "Bakiye yetersiz",
    "03": "Alacaklı hesap yok",
    "04": "Alıcı hesap bulunamadı",
    "08": "Borçlu hesap yok",
    "10": "Alıcı hesap kapalı veya blokeli",
    "71": "Alıcı ad soyad uyumsuz",
  };
  const generic = isGenericBankStatus(msg);
  if (known[code] && (generic || !msg)) return known[code];
  if (msg && !generic) return msg;
  const sent = sentRecipientName(it);
  if (sent.includes("*")) return `Hesap adı maskeli gönderildi: ${sent}`;
  if (nameDoubled(sent)) return `Hesap adı iki kez yapışık gönderildi: ${sent}`;
  if (String(it?.vakifbank_last_islem_durumu || "") === "IadeEdildi" || generic) {
    return sent
      ? `Banka gerekçe yazmadı. Gönderilen ad: ${sent}`
      : "Banka gerekçe yazmadı";
  }
  return msg;
}

function withdrawalSentBanner(it) {
  const s = String(it?.status || "").toLowerCase();
  if (s === "refunded" || it?.yandex_refunded) {
    const when = it?.yandex_refund_at ? safeDateLabel(it.yandex_refund_at) : "";
    const why = bankRefundReason(it);
    return {
      kind: "refunded",
      title: "Yandex iade edildi",
      detail: [why ? `Neden: ${why}` : "", when ? `İade saati: ${when}` : ""].filter(Boolean).join(" · "),
    };
  }
  if (isWithdrawalFailed(it)) {
    return { kind: "failed", title: "Başarısız", detail: withdrawalFailureReason(it) };
  }
  if (isWithdrawalSentOk(it)) {
    return {
      kind: "ok",
      title: isWalletPayout(it) ? "Cüzdana gönderildi" : "IBAN'a gönderildi",
      detail: "",
    };
  }
  if (s === "awaiting_payout" || s === "processing" || s === "approved") {
    return {
      kind: "pending",
      title: isWalletPayout(it) ? "Cüzdana gönderiliyor" : "IBAN'a gönderiliyor",
      detail: "",
    };
  }
  return {
    kind: "pending",
    title: isWalletPayout(it) ? "Sipay Cüzdan" : "IBAN",
    detail: "",
  };
}

function withdrawalDriverName(it) {
  // Listede IBAN / cüzdan hesap adı öncelikli (bankaya giden isim)
  const holder = String(it?.account_holder || "").trim();
  if (holder) return holder;
  const n = String(it?.driver_name || "").trim();
  if (n) return n;
  const fn = String(it?.driver_first_name || "").trim();
  const ln = String(it?.driver_last_name || "").trim();
  const combo = `${fn} ${ln}`.trim();
  if (combo) return combo;
  return "-";
}

function referralStatusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "invited") return "Davet edildi (henüz kayıt yok)";
  if (s === "registered") return "Kayıt oldu (yolculuk yok)";
  if (s === "active") return "Aktif (yolculuk var)";
  if (s === "reward_pending") return "Ödül bekliyor";
  if (s === "reward_paid") return "Ödül verildi";
  if (s === "rejected") return "Reddedildi";
  return s || "-";
}

function referralInviteeLabel(it) {
  const name = String(it?.invitee_name || "").trim();
  const phone = String(it?.invitee_phone || "").trim() || "-";
  return name ? `${name} • ${phone}` : phone;
}

function normalizeWithdrawalStatusForSelect(status) {
  const s = String(status || "").toLowerCase();
  if (s === "payout_pending" || s === "awaiting_payout" || s === "payout_sending") return "payout_pending";
  if (s === "payout_failed") return "payout_failed";
  if (s === "deduct_without_payout") return "deduct_without_payout";
  if (s === "refunded") return "refunded";
  if (s === "paid") return "paid";
  if (s === "approved") return "approved";
  if (s === "rejected" || s === "failed") return "rejected";
  if (s === "processing") return "processing";
  return "pending";
}

function localDateKey(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function monthStartDateKey(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
}

function withdrawalDatePresetRange(preset) {
  if (preset === "today") {
    const t = localDateKey();
    return { from: t, to: t };
  }
  if (preset === "month") {
    return { from: monthStartDateKey(), to: localDateKey() };
  }
  if (preset === "all") {
    return { from: "", to: "" };
  }
  return null;
}

function filterWithdrawalsByDateRange(list, dateFrom, dateTo) {
  const fromTs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
  const toTs = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;
  if (fromTs == null && toTs == null) return list;
  return list.filter((x) => {
    const t = x?.created_at ? new Date(x.created_at).getTime() : NaN;
    if (Number.isNaN(t)) return false;
    if (fromTs != null && !Number.isNaN(fromTs) && t < fromTs) return false;
    if (toTs != null && !Number.isNaN(toTs) && t > toTs) return false;
    return true;
  });
}

function localToIsoOrNull(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function isoToLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return (
      d.getFullYear() +
      "-" +
      pad(d.getMonth() + 1) +
      "-" +
      pad(d.getDate()) +
      "T" +
      pad(d.getHours()) +
      ":" +
      pad(d.getMinutes())
    );
  } catch {
    return "";
  }
}

function StatusPill({ item }) {
  const it = item || {};
  const now = Date.now();
  const s = it.start_at ? new Date(it.start_at).getTime() : null;
  const e = it.end_at ? new Date(it.end_at).getTime() : null;

  let label = "Aktif";
  if (it.is_deleted) label = "Silinmiş";
  else if (s && now < s) label = "Planlı";
  else if (e && now > e) label = "Süresi doldu";

  let style = { background: "#e9f7ef", border: "1px solid #bde5c8" };
  if (label === "Planlı") style = { background: "#eef3ff", border: "1px solid #c7d6ff" };
  if (label === "Süresi doldu") style = { background: "#fff4e6", border: "1px solid #ffd4a3" };
  if (label === "Silinmiş") style = { background: "#fdecec", border: "1px solid #f6b7b7" };

  return h(
    "span",
    {
      style: {
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 800,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        ...style,
      },
    },
    label,
    it.pinned && !it.is_deleted ? " • Sabit" : ""
  );
}

function IconBtn({ title, onClick, disabled, children }) {
  return h(
    "button",
    {
      type: "button",
      onClick,
      disabled,
      title,
      style: {
        width: 34,
        height: 34,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 10,
        border: "1px solid #e7e7e7",
        background: "#fff",
        color: "#111",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        padding: 0,
      },
    },
    children
  );
}

export default function AdminPanel() {
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem("admin_token") || "");
  const authed = !!adminToken;

  // Sol menü: announcements | rewards | referrals | withdrawals | mandates
  const [section, setSection] = useState("announcements");

  // announcements view: list | create | detail
  const [view, setView] = useState("list");
  const [selectedId, setSelectedId] = useState("");

  const [password, setPassword] = useState("");

  const [parks, setParks] = useState([]);
  const [items, setItems] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [selectedReferralId, setSelectedReferralId] = useState("");
  const [refStatus, setRefStatus] = useState("invited");
  const [refOrders, setRefOrders] = useState(0);
  const [refReward, setRefReward] = useState(0);
  const [refPaid, setRefPaid] = useState(false);
  const [refNote, setRefNote] = useState("");
  const [withdrawals, setWithdrawals] = useState([]);
  const [selectedWithdrawalId, setSelectedWithdrawalId] = useState("");
  const [wdStatus, setWdStatus] = useState("pending");
  const [wdNote, setWdNote] = useState("");
  const [wdFilter, setWdFilter] = useState("all");
  const [wdCityFilter, setWdCityFilter] = useState("all");
  const [wdRangePreset, setWdRangePreset] = useState("today"); // today | month | all | custom
  const [wdDateFrom, setWdDateFrom] = useState(() => localDateKey());
  const [wdDateTo, setWdDateTo] = useState(() => localDateKey());
  const [wdPhoneQuery, setWdPhoneQuery] = useState("");
  const [wdNameQuery, setWdNameQuery] = useState("");
  const [wdMethodFilter, setWdMethodFilter] = useState("all"); // all | iban | wallet
  const [rewardRules, setRewardRules] = useState([]);
  const [mandates, setMandates] = useState([]);
  const [mandateLogs, setMandateLogs] = useState([]);
  const [mandateFilter, setMandateFilter] = useState("all"); // all | ok | failed
  const [mandateFile, setMandateFile] = useState(null);
  const [mandateMsg, setMandateMsg] = useState("");
  const [mandateProgress, setMandateProgress] = useState(null); // {percent,done,total,ok,failed,current_*}
  const [mandateBatchId, setMandateBatchId] = useState("");
  const [mandatePaused, setMandatePaused] = useState(false);
  const [mandateSubView, setMandateSubView] = useState("excel"); // excel | schedules | add
  const [scheduleSearch, setScheduleSearch] = useState("");
  const [mandateParkFilter, setMandateParkFilter] = useState("all");
  const [mandateExportBusy, setMandateExportBusy] = useState(false);
  const [addPhone, setAddPhone] = useState("");
  const [addLookup, setAddLookup] = useState(null);
  const [addBusy, setAddBusy] = useState(false);
  const [addErr, setAddErr] = useState("");
  const [addIban, setAddIban] = useState("");
  const [addHolder, setAddHolder] = useState("");
  const [addNationalId, setAddNationalId] = useState("");
  const [addMethod, setAddMethod] = useState("iban");
  const [addHour1, setAddHour1] = useState("");
  const [addHour2On, setAddHour2On] = useState(false);
  const [addHour2, setAddHour2] = useState("");
  const [addFrequency, setAddFrequency] = useState("daily");
  const [addWeekday, setAddWeekday] = useState(0);
  const [addDayOfMonth, setAddDayOfMonth] = useState(1);
  const [editingId, setEditingId] = useState("");
  const [editMethod, setEditMethod] = useState("iban");
  const [editHour1, setEditHour1] = useState("");
  const [editHour2On, setEditHour2On] = useState(false);
  const [editHour2, setEditHour2] = useState("");
  const [editIban, setEditIban] = useState("");
  const [editHolder, setEditHolder] = useState("");
  const [filter, setFilter] = useState("all");

  const [payoutMaintenance, setPayoutMaintenance] = useState(false);
  const [driverIbanEnabled, setDriverIbanEnabled] = useState(false);
  const [autoWithdrawPollEnabled, setAutoWithdrawPollEnabled] = useState(false);
  const [maintenanceBusy, setMaintenanceBusy] = useState(false);
  const [maintenanceExemptParks, setMaintenanceExemptParks] = useState([]);
  const [velixOpen, setVelixOpen] = useState(false);
  const [payoutBalances, setPayoutBalances] = useState(null);
  const [payoutBalancesBusy, setPayoutBalancesBusy] = useState(false);

  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // form
  const [editId, setEditId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  // parks selection
  const [allParks, setAllParks] = useState(false);
  const [selectedParks, setSelectedParks] = useState([]);

  const [pinned, setPinned] = useState(false);
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");

  const parkOptions = useMemo(() => {
    if (!Array.isArray(parks)) return [];
    if (parks.length && typeof parks[0] === "object") {
      return parks
        .filter((x) => x && (x.key || x.label))
        .map((x) => ({ key: String(x.key), label: String(x.label || x.key) }));
    }
    return parks.map((k) => ({ key: String(k), label: String(k) }));
  }, [parks]);

  const parksLabelMap = useMemo(() => {
    const m = {};
    for (let i = 0; i < parkOptions.length; i++) {
      m[parkOptions[i].key] = parkOptions[i].label;
    }
    return m;
  }, [parkOptions]);

  const selectedParksText = useMemo(() => {
    if (allParks) return "Tümü (*)";
    const txt = (selectedParks || []).map((k) => parksLabelMap[k] || k).join(", ");
    return txt || "-";
  }, [allParks, selectedParks, parksLabelMap]);

  const selectedItem = useMemo(() => {
    if (!selectedId) return null;
    for (let i = 0; i < items.length; i++) {
      if (items[i] && items[i].id === selectedId) return items[i];
    }
    return null;
  }, [items, selectedId]);

  const filteredItems = useMemo(() => {
    const list = Array.isArray(items) ? items.slice() : [];
    list.sort((a, b) =>
      String(b && b.created_at ? b.created_at : "").localeCompare(String(a && a.created_at ? a.created_at : ""))
    );

    if (filter === "pinned") return list.filter((x) => x && x.pinned && !x.is_deleted);
    if (filter === "deleted") return list.filter((x) => x && x.is_deleted);

    if (filter === "active") {
      const now = Date.now();
      return list.filter((x) => {
        if (!x || x.is_deleted) return false;
        const s = x.start_at ? new Date(x.start_at).getTime() : null;
        const e = x.end_at ? new Date(x.end_at).getTime() : null;
        if (s && now < s) return false;
        if (e && now > e) return false;
        return true;
      });
    }

    const a = list.filter((x) => x && !x.is_deleted);
    const d = list.filter((x) => x && x.is_deleted);
    return a.concat(d);
  }, [items, filter]);

  const scopedWithdrawals = useMemo(() => {
    let list = dedupeWithdrawalsById(Array.isArray(withdrawals) ? withdrawals.slice() : []);
    if (wdCityFilter !== "all") {
      list = list.filter((x) => String(x?.park_key || "") === wdCityFilter);
    }
    if (wdRangePreset !== "all") {
      list = filterWithdrawalsByDateRange(list, wdDateFrom, wdDateTo);
    }
    const phoneQ = String(wdPhoneQuery || "").replace(/\D/g, "");
    if (phoneQ) {
      list = list.filter((x) => phoneFilterMatches(x?.phone, phoneQ));
    }
    const nameQ = String(wdNameQuery || "").trim().toLocaleLowerCase("tr");
    if (nameQ) {
      list = list.filter((x) => {
        const driver = withdrawalDriverName(x).toLocaleLowerCase("tr");
        const holder = String(x?.account_holder || "").toLocaleLowerCase("tr");
        return driver.includes(nameQ) || holder.includes(nameQ);
      });
    }
    if (wdMethodFilter === "iban") {
      list = list.filter((x) => !isWalletPayout(x));
    } else if (wdMethodFilter === "wallet") {
      list = list.filter((x) => isWalletPayout(x));
    }
    return list;
  }, [withdrawals, wdCityFilter, wdRangePreset, wdDateFrom, wdDateTo, wdPhoneQuery, wdNameQuery, wdMethodFilter]);

  const filteredWithdrawals = useMemo(() => {
    const list = scopedWithdrawals;
    if (wdFilter === "paid") return list.filter((x) => isWithdrawalDefinitelyPaid(x));
    if (wdFilter === "failed") return list.filter((x) => isWithdrawalFailed(x));
    if (wdFilter === "payout_pending") return list.filter((x) => isWithdrawalBankPending(x));
    if (wdFilter === "sending") return list.filter((x) => isWithdrawalSending(x));
    return list;
  }, [scopedWithdrawals, wdFilter]);

  const withdrawalStats = useMemo(() => {
    const list = Array.isArray(scopedWithdrawals) ? scopedWithdrawals : [];
    let paid = 0;
    let failed = 0;
    let bankPending = 0;
    let sending = 0;
    for (let i = 0; i < list.length; i++) {
      if (isWithdrawalDefinitelyPaid(list[i])) paid += 1;
      else if (isWithdrawalFailed(list[i])) failed += 1;
      else if (isWithdrawalBankPending(list[i])) bankPending += 1;
      if (isWithdrawalSending(list[i])) sending += 1;
    }
    return { total: list.length, paid, failed, bankPending, sending };
  }, [scopedWithdrawals]);

  const withdrawalCities = useMemo(() => {
    return (parkOptions || [])
      .map((p) => String(p?.key || ""))
      .filter(Boolean)
      .sort((a, b) => (parksLabelMap[a] || a).localeCompare(parksLabelMap[b] || b, "tr"));
  }, [parkOptions, parksLabelMap]);

  const mandateLogStats = useMemo(() => {
    const list = Array.isArray(mandateLogs) ? mandateLogs : [];
    let ok = 0;
    let failed = 0;
    for (const x of list) {
      if (mandateMoneySent(x)) ok += 1;
      else failed += 1;
    }
    return { total: list.length, ok, failed };
  }, [mandateLogs]);

  const mandateParkOptions = useMemo(() => {
    const labels = {};
    for (const it of Array.isArray(mandates) ? mandates : []) {
      const key = String(it?.park_key || "");
      if (!key || labels[key]) continue;
      labels[key] = String(it?.park_label || parksLabelMap[key] || key);
    }
    for (const p of parkOptions || []) {
      const key = String(p?.key || "");
      if (!key) continue;
      if (!labels[key]) labels[key] = String(p?.label || parksLabelMap[key] || key);
    }
    return Object.keys(labels).sort((a, b) => labels[a].localeCompare(labels[b], "tr")).map((key) => ({
      key,
      label: labels[key],
    }));
  }, [mandates, parkOptions, parksLabelMap]);

  const filteredMandateSchedules = useMemo(() => {
    let list = Array.isArray(mandates) ? mandates.slice() : [];
    if (mandateParkFilter !== "all") {
      list = list.filter((it) => String(it?.park_key || "") === mandateParkFilter);
    }
    const q = String(scheduleSearch || "").trim().toLocaleLowerCase("tr");
    const digits = q.replace(/\D/g, "");
    if (!q) return list;
    return list.filter((it) => {
      const phone = phoneDigitsLast10(it?.phone);
      const qDigits = phoneDigitsLast10(digits);
      const holder = String(it?.account_holder || "").toLocaleLowerCase("tr");
      return holder.includes(q) || (qDigits && phone.includes(qDigits));
    });
  }, [mandates, scheduleSearch, mandateParkFilter]);

  async function lookupMandateDriver() {
    setAddErr("");
    setAddLookup(null);
    const phone = normalizePhoneInput(addPhone);
    if (!phone) {
      setAddErr("Telefon numarası girin.");
      return;
    }
    setAddBusy(true);
    try {
      const res = await adminLookupMandateDriver(phone);
      setAddLookup(res);
      setAddHolder(res?.account_holder || "");
      setAddIban(filledMandateIban(res) || res?.iban || "");
      setAddNationalId(res?.national_id || "");
      setAddMethod(String(res?.payout_method || "iban") === "wallet" ? "wallet" : "iban");
      setAddFrequency("daily");
      setAddWeekday(0);
      setAddDayOfMonth(1);
      setAddHour1("");
      setAddHour2On(false);
      setAddHour2("");
    } catch (e2) {
      setAddErr(e2.message || "Sürücü bulunamadı");
    } finally {
      setAddBusy(false);
    }
  }

  async function submitAddMandate() {
    if (!addLookup) {
      setAddErr("Önce telefon ile sürücü arayın.");
      return;
    }
    if (addLookup.has_active_mandate) {
      setAddErr("Bu sürücünün zaten aktif otomatik ödeme talimatı var. Listeden düzenleyin.");
      return;
    }
    const hours = hoursPayloadFromInputs(addHour1, addHour2On, addHour2);
    if (hours.error) {
      setAddErr(hours.error);
      return;
    }
    setAddErr("");
    setAddBusy(true);
    try {
      const payload = {
        phone: addLookup.phone || addPhone,
        account_holder: addHolder || addLookup.account_holder || "",
        payout_method: addMethod,
        frequency: addFrequency,
        weekday: addFrequency === "weekly" ? Number(addWeekday) : null,
        day_of_month: addFrequency === "monthly" ? Number(addDayOfMonth) : null,
        ...hours.payload,
      };
      if (addMethod !== "wallet") {
        payload.iban = addIban;
        payload.national_id = addNationalId;
      }
      await adminAddPaymentMandate(payload);
      const md = await adminListPaymentMandates();
      setMandates((md && md.items) || []);
      setMandateLogs((md && md.logs) || []);
      setMandateMsg(`${addLookup.account_holder || addHolder || addLookup.phone} için talimat eklendi.`);
      setMandateSubView("schedules");
      setAddPhone("");
      setAddLookup(null);
      setAddIban("");
      setAddHolder("");
      setAddNationalId("");
      setAddMethod("iban");
      setAddFrequency("daily");
      setAddWeekday(0);
      setAddDayOfMonth(1);
      setAddHour1("");
      setAddHour2On(false);
      setAddHour2("");
    } catch (e2) {
      setAddErr(e2.message || "Talimat eklenemedi");
    } finally {
      setAddBusy(false);
    }
  }

  const filteredMandateLogs = useMemo(() => {
    const list = Array.isArray(mandateLogs) ? mandateLogs : [];
    if (mandateFilter === "ok") return list.filter((x) => mandateMoneySent(x));
    if (mandateFilter === "failed") return list.filter((x) => !mandateMoneySent(x));
    return list;
  }, [mandateLogs, mandateFilter]);

  const channelOutflow = useMemo(() => {
    let list = Array.isArray(withdrawals) ? withdrawals : [];
    if (wdCityFilter !== "all") {
      list = list.filter((x) => String(x?.park_key || "") === wdCityFilter);
    }
    if (wdRangePreset !== "all") {
      list = filterWithdrawalsByDateRange(list, wdDateFrom, wdDateTo);
    }
    let sipayNet = 0;
    let sipayCount = 0;
    let vakifNet = 0;
    let vakifCount = 0;
    for (const it of list) {
      if (!isWithdrawalDefinitelyPaid(it)) continue;
      const net = withdrawalNetSent(it);
      if (isWalletPayout(it)) {
        sipayNet += net;
        sipayCount += 1;
      } else if (isVakifbankAccountPayout(it)) {
        vakifNet += net;
        vakifCount += 1;
      }
    }
    return { sipayNet, sipayCount, vakifNet, vakifCount };
  }, [withdrawals, wdCityFilter, wdRangePreset, wdDateFrom, wdDateTo]);

  const withdrawalReport = useMemo(() => {
    const list = Array.isArray(scopedWithdrawals) ? scopedWithdrawals : [];
    let totalApprovedNet = 0;
    let todayPaid = 0;
    let totalCommission = 0;
    const byCity = {};

    for (const it of list) {
      const cityKey = String(it?.park_key || "unknown");
      const cityLabel = parksLabelMap[cityKey] || cityKey;
      const gross = Number(it?.amount || 0) || 0;
      const fee = Number(it?.fee || 0) || 0;
      const paid = Number(it?.payout_amount ?? it?.net_amount ?? Math.max(0, Number(it?.amount || 0) - Number(it?.fee || 0))) || 0;
      const status = String(it?.status || "").toLowerCase();
      const isPaid = status === "paid";
      const isApprovedLike =
        status === "approved" ||
        status === "processing" ||
        status === "awaiting_payout" ||
        status === "payout_sending" ||
        status === "payout_pending" ||
        status === "paid";

      // Seçili filtre kapsamındaki ana KPI'lar
      if (isApprovedLike) totalApprovedNet += paid;
      if (isApprovedLike) totalCommission += fee;
      if (isPaid) todayPaid += paid;

      if (!byCity[cityKey]) {
        byCity[cityKey] = { cityKey, cityLabel, reqCount: 0, reqTotal: 0, paidTotal: 0 };
      }
      byCity[cityKey].reqCount += 1;
      byCity[cityKey].reqTotal += gross;
      if (isPaid) byCity[cityKey].paidTotal += paid;
    }

    const cityRows = Object.values(byCity).sort((a, b) => b.reqTotal - a.reqTotal);
    return {
      totalApprovedNet,
      totalCommission,
      todayPaid,
      cityRows,
    };
  }, [scopedWithdrawals, parksLabelMap]);

  function buildWithdrawalsApiFilters() {
    const f = { include_pending: 1 };
    if (wdRangePreset !== "all") {
      f.date_from = wdDateFrom || localDateKey();
      f.date_to = wdDateTo || localDateKey();
    }
    const phoneQ = String(wdPhoneQuery || "").replace(/\D/g, "");
    if (phoneQ.length >= 7) f.phone = phoneQ;
    const nameQ = String(wdNameQuery || "").trim();
    if (nameQ) f.name = nameQ;
    if (wdCityFilter && wdCityFilter !== "all") f.park_key = wdCityFilter;
    if (wdMethodFilter === "iban" || wdMethodFilter === "wallet") f.payout_method = wdMethodFilter;
    return f;
  }

  async function reloadAll() {
    setErr("");
    const p = await adminFetchParks();
    setParks((p && p.parks) || (p && p.items) || p || []);
    const a = await adminListAnnouncements();
    setItems((a && a.items) || []);
    const r = await adminListReferrals();
    setReferrals((r && r.items) || []);
    const wdFilters = buildWithdrawalsApiFilters();
    const w = await adminListWithdrawals(wdFilters);
    setWithdrawals(dedupeWithdrawalsById((w && w.items) || []));
    const rw = await adminGetRewards();
    setRewardRules(Array.isArray(rw?.rules) ? rw.rules : []);
    const md = await adminListPaymentMandates();
    setMandates((md && md.items) || []);
    setMandateLogs((md && md.logs) || []);
    try {
      const pm = await adminGetPayoutMaintenance();
      setPayoutMaintenance(!!pm?.maintenance);
      setDriverIbanEnabled(!!pm?.driver_iban_enabled);
      setAutoWithdrawPollEnabled(!!pm?.auto_withdraw_poll_enabled);
      setMaintenanceExemptParks(Array.isArray(pm?.maintenance_exempt_parks) ? pm.maintenance_exempt_parks : []);
      setVelixOpen(!!pm?.velix_open);
    } catch {}
  }

  function applyMaintenanceResponse(res) {
    setPayoutMaintenance(!!res?.maintenance);
    setDriverIbanEnabled(!!res?.driver_iban_enabled);
    setAutoWithdrawPollEnabled(!!res?.auto_withdraw_poll_enabled);
    setMaintenanceExemptParks(Array.isArray(res?.maintenance_exempt_parks) ? res.maintenance_exempt_parks : []);
    setVelixOpen(!!res?.velix_open);
  }

  async function toggleMaintenance() {
    if (maintenanceBusy) return;
    const wantMaintenance = !payoutMaintenance;
    const action = wantMaintenance
      ? "Tüm para çekimini bakıma almak (Velix Go dahil)"
      : "Bakımdan çıkarmak (IBAN, Sipay cüzdan ve otomatik talimat açılacak)";
    if (!window.confirm(`${action} istediğinize emin misiniz?`)) return;
    setMaintenanceBusy(true);
    try {
      const res = await adminSetPayoutMaintenance(wantMaintenance);
      applyMaintenanceResponse(res);
    } catch (e2) {
      setErr(e2?.message || "Bakım toggle hatası");
    } finally {
      setMaintenanceBusy(false);
    }
  }

  async function toggleVelixMaintenance() {
    if (maintenanceBusy) return;
    const wantMaintenance = velixOpen;
    const action = wantMaintenance
      ? "Yalnızca Velix bakıma alınacak. Diğer parklar aynı kalacak."
      : "Yalnızca Velix bakımdan çıkarılacak. Diğer parklar bakımda kalacak. Otomatik talimatlar da yalnız Velix için çalışır.";
    if (!window.confirm(`${action} Emin misiniz?`)) return;
    setMaintenanceBusy(true);
    try {
      const res = await adminSetVelixMaintenance(wantMaintenance);
      applyMaintenanceResponse(res);
    } catch (e2) {
      setErr(e2?.message || "Velix bakım hatası");
    } finally {
      setMaintenanceBusy(false);
    }
  }

  useEffect(() => {
    if (!authed || section !== "withdrawals") return;
    let stop = false;
    const poll = async () => {
      try {
        const pm = await adminGetPayoutMaintenance();
        if (!stop) {
          setPayoutMaintenance(!!pm?.maintenance);
          setDriverIbanEnabled(!!pm?.driver_iban_enabled);
          setAutoWithdrawPollEnabled(!!pm?.auto_withdraw_poll_enabled);
          setMaintenanceExemptParks(Array.isArray(pm?.maintenance_exempt_parks) ? pm.maintenance_exempt_parks : []);
          setVelixOpen(!!pm?.velix_open);
        }
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 5000);
    return () => { stop = true; clearInterval(iv); };
  }, [authed, section]);

  // Tarih / telefon / isim filtresi değişince listeyi yükle
  useEffect(() => {
    if (!authed || section !== "withdrawals") return;
    let stop = false;
    (async () => {
      try {
        const w = await adminListWithdrawals(buildWithdrawalsApiFilters());
        if (!stop) setWithdrawals(dedupeWithdrawalsById((w && w.items) || []));
      } catch {}
    })();
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, section, wdRangePreset, wdDateFrom, wdDateTo, wdPhoneQuery, wdNameQuery, wdCityFilter, wdMethodFilter]);

  // Sarı IBAN yeşile dönünce F5'siz güncelle — sadece tarih filtreli hafif poll
  useEffect(() => {
    if (!authed || section !== "withdrawals") return;
    if (wdRangePreset === "all") return; // tüm geçmişi periyodik çekme — paneli kilitler
    let stop = false;
    let inFlight = false;
    const refreshWithdrawals = async () => {
      if (stop || inFlight) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      inFlight = true;
      try {
        const w = await adminListWithdrawals(buildWithdrawalsApiFilters());
        if (!stop) setWithdrawals(dedupeWithdrawalsById((w && w.items) || []));
      } catch {
      } finally {
        inFlight = false;
      }
    };
    const iv = setInterval(refreshWithdrawals, 15000);
    const onVis = () => {
      if (document.visibilityState === "visible") refreshWithdrawals();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, section, wdRangePreset, wdDateFrom, wdDateTo, wdPhoneQuery, wdNameQuery, wdCityFilter, wdMethodFilter]);

  useEffect(() => {
    if (!authed || section !== "withdrawals") return;
    let stop = false;
    const loadBalances = async () => {
      setPayoutBalancesBusy(true);
      try {
        const res = await adminGetPayoutBalances();
        if (!stop) setPayoutBalances(res || null);
      } catch (e) {
        if (!stop) setPayoutBalances(null);
      } finally {
        if (!stop) setPayoutBalancesBusy(false);
      }
    };
    loadBalances();
    const iv = setInterval(loadBalances, 60000);
    return () => { stop = true; clearInterval(iv); };
  }, [authed, section]);

  useEffect(() => {
    if (!authed) return;
    reloadAll().catch((e) => setErr((e && e.message) || "Yüklenemedi"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  async function doLogin(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = await adminLogin(password);
      localStorage.setItem("admin_token", res.token);
      setAdminToken(res.token);
      setPassword("");
      setSection("announcements");
      setView("list");
    } catch (e2) {
      setErr((e2 && e2.message) || "Login hatası");
    } finally {
      setBusy(false);
    }
  }

  function doLogout() {
    localStorage.removeItem("admin_token");
    setAdminToken("");
    setSection("announcements");
    setView("list");
    setSelectedId("");
  }

  function resetForm() {
    setEditId("");
    setTitle("");
    setBody("");
    setPinned(false);
    setStartAt("");
    setEndAt("");
    setAllParks(false);
    setSelectedParks([]);
  }

  function openCreate() {
    setErr("");
    resetForm();
    setView("create");
  }

  function openEditFromItem(it) {
    setErr("");
    setEditId(it.id);
    setTitle(it.title || "");
    setBody(it.body || "");
    setPinned(!!it.pinned);
    setStartAt(isoToLocalInput(it.start_at));
    setEndAt(isoToLocalInput(it.end_at));

    if (Array.isArray(it.parks) && it.parks.indexOf("*") >= 0) {
      setAllParks(true);
      setSelectedParks([]);
    } else {
      setAllParks(false);
      setSelectedParks(Array.isArray(it.parks) ? it.parks : []);
    }

    setView("create");
  }

  async function submitForm(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        title: title || "",
        body: body || "",
        parks: allParks ? ["*"] : selectedParks,
        pinned: !!pinned,
        start_at: localToIsoOrNull(startAt),
        end_at: localToIsoOrNull(endAt),
      };

      if (editId) await adminUpdateAnnouncement(editId, payload);
      else await adminCreateAnnouncement(payload);

      resetForm();
      await reloadAll();
      setView("list");
    } catch (e2) {
      setErr((e2 && e2.message) || "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  }

  async function softDelete(id) {
    if (!window.confirm("Duyuruyu silmek istiyor musun?")) return;
    setErr("");
    setBusy(true);
    try {
      await adminDeleteAnnouncement(id);
      await reloadAll();
      if (selectedId === id) {
        setSelectedId("");
        setView("list");
      }
    } catch (e2) {
      setErr((e2 && e2.message) || "Silinemedi");
    } finally {
      setBusy(false);
    }
  }

  async function togglePinned(it) {
    setErr("");
    setBusy(true);
    try {
      await adminUpdateAnnouncement(it.id, { pinned: !it.pinned });
      await reloadAll();
    } catch (e2) {
      setErr((e2 && e2.message) || "Güncellenemedi");
    } finally {
      setBusy(false);
    }
  }

  function parksText(pks) {
    if (Array.isArray(pks) && pks.indexOf("*") >= 0) return "Tümü (*)";
    const arr = Array.isArray(pks) ? pks : [];
    const txt = arr.map((k) => parksLabelMap[k] || k).join(", ");
    return txt || "-";
  }

  function openReferralDetail(it) {
    setSelectedReferralId(it.id || "");
    setRefStatus(String(it.status || "invited"));
    setRefOrders(Number(it.orders_count || 0));
    setRefReward(Number(it.reward_amount || 0));
    setRefPaid(!!it.reward_paid);
    setRefNote(String(it.note || ""));
  }

  async function saveReferral() {
    if (!selectedReferralId) return;
    setErr("");
    setBusy(true);
    try {
      await adminUpdateReferral(selectedReferralId, {
        status: refStatus,
        orders_count: Number(refOrders || 0),
        reward_amount: Number(refReward || 0),
        reward_paid: !!refPaid,
        note: refNote,
      });
      const r = await adminListReferrals();
      setReferrals((r && r.items) || []);
    } catch (e2) {
      setErr((e2 && e2.message) || "Referral güncellenemedi");
    } finally {
      setBusy(false);
    }
  }

  function openWithdrawalDetail(it) {
    setSelectedWithdrawalId(it.id || "");
    setWdStatus(normalizeWithdrawalStatusForSelect(it.status));
    setWdNote(String(it.note || ""));
  }

  async function saveWithdrawal() {
    if (!selectedWithdrawalId) return;
    setErr("");
    setBusy(true);
    try {
      await adminUpdateWithdrawal(selectedWithdrawalId, {
        status: wdStatus,
        note: wdNote,
        auto_send: false,
      });
      const w = await adminListWithdrawals(buildWithdrawalsApiFilters());
      setWithdrawals(dedupeWithdrawalsById((w && w.items) || []));
    } catch (e2) {
      setErr((e2 && e2.message) || "Para çekim güncellenemedi");
    } finally {
      setBusy(false);
    }
  }

  function addRewardRule() {
    setRewardRules((prev) => {
      const arr = Array.isArray(prev) ? prev.slice() : [];
      arr.push({ id: `r_${Date.now()}`, from_rank: 1, to_rank: 1, amount: 0 });
      return arr;
    });
  }

  function removeRewardRule(id) {
    setRewardRules((prev) => (Array.isArray(prev) ? prev.filter((x) => x.id !== id) : []));
  }

  function updateRewardRule(id, key, value) {
    setRewardRules((prev) =>
      (Array.isArray(prev) ? prev : []).map((x) => (x.id === id ? { ...x, [key]: value } : x))
    );
  }

  async function saveRewardRules() {
    setErr("");
    setBusy(true);
    try {
      const payload = {
        rules: (rewardRules || []).map((r) => ({
          id: r.id,
          from_rank: Number(r.from_rank || 0),
          to_rank: Number(r.to_rank || 0),
          amount: Number(r.amount || 0),
        })),
      };
      const res = await adminSetRewards(payload);
      setRewardRules(Array.isArray(res?.rules) ? res.rules : []);
    } catch (e2) {
      setErr((e2 && e2.message) || "Ödül kuralları kaydedilemedi");
    } finally {
      setBusy(false);
    }
  }

  async function uploadMandates() {
    if (!mandateFile) {
      setErr("Excel dosyası seçin.");
      return;
    }
    setErr("");
    setMandateMsg("");
    setBusy(true);
    setMandatePaused(false);
    setMandateProgress({
      percent: 0,
      done: 0,
      total: 0,
      ok: 0,
      failed: 0,
      paid: 0,
      pay_skipped: 0,
      pay_failed: 0,
      status: "starting",
      current_phone: "",
      current_holder: "",
      current_row: null,
      current_status: "Dosya yükleniyor...",
      current_error: "",
      recent_logs: [],
    });
    try {
      const start = await adminUploadPaymentMandates(mandateFile);
      const batchId = start?.batch_id;
      if (!batchId) throw new Error("Yükleme işi başlatılamadı.");
      setMandateBatchId(batchId);
      setMandateMsg(`İşleniyor: ${start?.filename || mandateFile.name}`);
      setMandateFile(null);

      // Satır satır ilerlemeyi poll et
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const p = await adminPaymentMandateUploadProgress(batchId);
        setMandateProgress(p);
        setMandatePaused(String(p?.status || "") === "paused");
        const extra = []
        .concat(Array.isArray(p?.recent_logs) ? p.recent_logs : [])
        .concat(Array.isArray(p?.failed_logs) ? p.failed_logs : []);
      if (extra.length) {
        setMandateLogs((prev) => {
          const map = {};
          for (const x of Array.isArray(prev) ? prev : []) map[x.id] = x;
          for (const x of extra) map[x.id] = x;
          return Object.values(map).sort((a, b) =>
            String(b.created_at || "").localeCompare(String(a.created_at || ""))
          );
        });
      }
        if (p?.status === "done" || p?.status === "error" || p?.status === "cancelled") {
          if (p.status === "error") {
            setErr(p.error || "Yükleme sırasında hata oluştu");
          }
          const errN = Number(p.failed || 0);
          const paidN = Number(p.paid || p.ok || 0);
          setMandateMsg(
            `${paidN} kişi İşyerimPOS ile gönderildi, ${errN} başarısız. Her gönderimde API IsDone beklendi (aynı anda 4 kişi).`
          );
          if (errN) setMandateFilter("failed");
          const md = await adminListPaymentMandates();
          setMandates((md && md.items) || []);
          setMandateLogs((md && md.logs) || []);
          setMandatePaused(false);
          break;
        }
        await new Promise((r) => setTimeout(r, 700));
      }
    } catch (e2) {
      setErr((e2 && e2.message) || "Excel yüklenemedi");
      setMandateProgress(null);
      setMandateBatchId("");
    } finally {
      setBusy(false);
    }
  }

  async function downloadFailedMandateExcel() {
    const batchId = mandateBatchId;
    if (!batchId) {
      setErr("İndirmek için önce Excel yükleyin.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await adminDownloadMandateFailedExcel(batchId);
    } catch (e2) {
      setErr((e2 && e2.message) || "Başarısız excel indirilemedi");
    } finally {
      setBusy(false);
    }
  }

  async function controlMandateUpload(action) {
    if (!mandateBatchId) return;
    try {
      const res = await adminPaymentMandateUploadControl(mandateBatchId, action);
      setMandatePaused(String(res?.status || "") === "paused");
      if (res?.job) setMandateProgress(res.job);
      if (action === "pause") setMandateMsg("Yükleme duraklatıldı. Devam Et ile sürdürülür.");
      if (action === "resume") setMandateMsg("Yükleme devam ediyor...");
      if (action === "cancel") setMandateMsg("Yükleme iptal edildi.");
    } catch (e2) {
      setErr((e2 && e2.message) || "Kontrol başarısız");
    }
  }

  async function clearAllMandates() {
    if (!window.confirm("Excel/admin talimatları ve yükleme logları silinsin mi? Sürücülerin kendi talimatları durur.")) return;
    setBusy(true);
    setErr("");
    try {
      await adminClearPaymentMandates();
      setMandates([]);
      setMandateLogs([]);
      setMandateProgress(null);
      setMandateMsg("Liste temizlendi. Yeni Excel yükleyebilirsiniz.");
    } catch (e2) {
      setErr((e2 && e2.message) || "Temizlenemedi");
    } finally {
      setBusy(false);
    }
  }

  async function toggleMandate(it) {
    setBusy(true);
    setErr("");
    try {
      await adminUpdatePaymentMandate(it.id, { enabled: !it.enabled });
      const md = await adminListPaymentMandates();
      setMandates((md && md.items) || []);
      setMandateLogs((md && md.logs) || []);
    } catch (e2) {
      setErr((e2 && e2.message) || "Güncellenemedi");
    } finally {
      setBusy(false);
    }
  }

  async function removeMandate(it) {
    if (!window.confirm(`${it.account_holder || it.phone || ""} için otomatik ödeme talimatı kaldırılsın mı?`)) return;
    setBusy(true);
    setErr("");
    try {
      await adminDeletePaymentMandate(it.id);
      const md = await adminListPaymentMandates();
      setMandates((md && md.items) || []);
      setMandateLogs((md && md.logs) || []);
      if (editingId === it.id) setEditingId("");
    } catch (e2) {
      setErr((e2 && e2.message) || "Silinemedi");
    } finally {
      setBusy(false);
    }
  }

  function startEditMandate(it) {
    setErr("");
    setEditingId(it.id);
    setEditMethod(String(it.payout_method) === "wallet" ? "wallet" : "iban");
    setEditHour1(hhmm(it.morning_hour ?? 9, it.morning_minute ?? 0));
    setEditHour2On(!!it.second_slot_enabled);
    setEditHour2(it.second_slot_enabled ? hhmm(it.evening_hour ?? 21, it.evening_minute ?? 0) : "");
    setEditIban(filledMandateIban(it));
    setEditHolder(String(it.account_holder || "").trim());
  }

  async function saveEditMandate(it) {
    const hours = hoursPayloadFromInputs(editHour1, editHour2On, editHour2);
    if (hours.error) {
      setErr(hours.error);
      return;
    }
    if (editMethod !== "wallet") {
      const holder = String(editHolder || "").trim();
      if (!holder) {
        setErr("IBAN için ad soyad gerekli.");
        return;
      }
    }
    setBusy(true);
    setErr("");
    try {
      const payload = {
        payout_method: editMethod,
        ...hours.payload,
      };
      if (editMethod !== "wallet") {
        payload.iban = editIban || it.iban || "";
        payload.account_holder = String(editHolder || "").trim();
      }
      await adminUpdatePaymentMandate(it.id, payload);
      const md = await adminListPaymentMandates();
      setMandates((md && md.items) || []);
      setMandateLogs((md && md.logs) || []);
      setEditingId("");
      setMandateMsg(`${String(editHolder || "").trim() || it.account_holder || it.phone || ""} talimatı güncellendi.`);
    } catch (e2) {
      setErr((e2 && e2.message) || "Güncellenemedi");
    } finally {
      setBusy(false);
    }
  }

  // LOGIN
  if (!authed) {
    return h(
      "div",
      { className: "page login-bg" },
      h(
        "div",
        { className: "card login-card" },
        h(
          "div",
          { className: "brand" },
          h("img", { src: LOGO_SRC, alt: "SiTaxi", className: "brand-logo" }),
          h("div", { className: "brand-sub" }, "Admin Panel")
        ),
        h("h1", { className: "title" }, "Admin Giriş"),
        h(
          "form",
          { className: "form", onSubmit: doLogin },
          h("label", { className: "label" }, "Admin Şifresi"),
          h("input", {
            className: "input",
            type: "password",
            value: password,
            onChange: (e) => setPassword(e.target.value),
            placeholder: "Şifre",
          }),
          err ? h("div", { className: "error" }, err) : null,
          h("button", { className: "btn", disabled: busy, type: "submit" }, busy ? "Giriş..." : "Giriş Yap")
        ),
        h("div", { className: "muted", style: { marginTop: 10 } }, "URL: /admin")
      )
    );
  }

  // MAIN
  return h(
    "div",
    { className: "home-wrap admin-shell" },

    h(
      "aside",
      { className: "sidebar" },
      h("img", { src: LOGO_SRC, alt: "SiTaxi", className: "sidebar-logo" }),
      h(
        "nav",
        { className: "menu" },

        h(
          "button",
          {
            type: "button",
            className: "menu-item" + (section === "announcements" ? " active" : ""),
            onClick: () => {
              setErr("");
              setSection("announcements");
              setView("list");
              setSelectedId("");
            },
          },
          "Duyurular"
        ),

        h(
          "button",
          {
            type: "button",
            className: "menu-item" + (section === "rewards" ? " active" : ""),
            onClick: () => {
              setErr("");
              setSection("rewards");
              setView("list");
              setSelectedId("");
            },
          },
          "Ödül Belirleme"
        ),
        h(
          "button",
          {
            type: "button",
            className: "menu-item" + (section === "referrals" ? " active" : ""),
            onClick: () => {
              setErr("");
              setSection("referrals");
              setView("list");
              setSelectedId("");
            },
          },
          "Arkadaşını Getir Sonuçları"
        ),
        h(
          "button",
          {
            type: "button",
            className: "menu-item" + (section === "withdrawals" ? " active" : ""),
            onClick: () => {
              setErr("");
              setSection("withdrawals");
              setView("list");
              setSelectedId("");
            },
          },
          "Para Çekim"
        ),
        h(
          "button",
          {
            type: "button",
            className: "menu-item" + (section === "mandates" ? " active" : ""),
            onClick: () => {
              setErr("");
              setSection("mandates");
              setView("list");
              setSelectedId("");
            },
          },
          "Ödeme Talimatı"
        ),
        h(
          "button",
          {
            type: "button",
            className: "menu-item" + (section === "vakifbank_ty_payments" ? " active" : ""),
            onClick: () => {
              setErr("");
              setSection("vakifbank_ty_payments");
              setView("list");
              setSelectedId("");
            },
          },
          "Ödeme-Taksi Yönetim"
        ),
        h(
          "button",
          { type: "button", className: "btn-outline", onClick: () => reloadAll().catch(() => {}) },
          "Yenile"
        )
      ),
      h("button", { className: "btn-outline", onClick: doLogout }, "Çıkış")
    ),

    h(
      "main",
      { className: "content admin-content" },

      h(
        "div",
        { className: "topbar" },
        h("div", { className: "hello" }, h("span", { className: "name" }, "Admin Panel")),
        h(
          "div",
          { className: "muted" },
          section === "rewards"
            ? "Aylık Ödül Kuralları"
            : section === "referrals"
            ? "Arkadaşını Getir Takibi"
            : section === "withdrawals"
            ? "Para Çekim Yönetimi"
            : section === "mandates"
            ? "Ödeme Talimatı"
            : section === "vakifbank_ty_payments"
            ? "Ödeme — Taksi Yönetim (Yandex yok)"
            : "Duyuru Yönetimi"
        )
      ),

      err
        ? h(
            "div",
            { className: "card info-card", style: { marginTop: 12, borderLeft: "6px solid #ff6b6b" } },
            h("div", { className: "muted" }, err)
          )
        : null,

      // REWARDS
      section === "rewards"
        ? h(
            "div",
            { className: "card info-card", style: { marginTop: 14 } },
            h("div", { className: "card-title", style: { fontWeight: 900, fontSize: 18 } }, "Aylık Ödül Belirleme"),
            h("div", { className: "muted", style: { marginTop: 6 } }, "Sıra aralığına göre aylık ödül tutarını belirleyin."),
            h(
              "div",
              { style: { marginTop: 10, display: "grid", gap: 8 } },
              (rewardRules || []).map((r) =>
                h(
                  "div",
                  {
                    key: r.id,
                    style: {
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr auto",
                      gap: 8,
                      alignItems: "center",
                    },
                  },
                  h("input", {
                    className: "input",
                    type: "number",
                    min: 1,
                    value: r.from_rank,
                    onChange: (e) => updateRewardRule(r.id, "from_rank", e.target.value),
                    placeholder: "Başlangıç sıra",
                  }),
                  h("input", {
                    className: "input",
                    type: "number",
                    min: 1,
                    value: r.to_rank,
                    onChange: (e) => updateRewardRule(r.id, "to_rank", e.target.value),
                    placeholder: "Bitiş sıra",
                  }),
                  h("input", {
                    className: "input",
                    type: "number",
                    min: 0,
                    step: "0.01",
                    value: r.amount,
                    onChange: (e) => updateRewardRule(r.id, "amount", e.target.value),
                    placeholder: "Ödül (₺)",
                  }),
                  h(
                    "button",
                    { type: "button", className: "btn-outline", onClick: () => removeRewardRule(r.id) },
                    "Sil"
                  )
                )
              )
            ),
            h(
              "div",
              { style: { display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" } },
              h("button", { type: "button", className: "btn-outline", onClick: addRewardRule }, "+ Kural Ekle"),
              h(
                "button",
                { type: "button", className: "btn", disabled: busy, onClick: saveRewardRules },
                busy ? "Kaydediliyor..." : "Ödül Kurallarını Kaydet"
              )
            )
          )
        : null,

      // REFERRALS
      section === "referrals"
        ? h(
            "div",
            { className: "card info-card", style: { marginTop: 14 } },
            h(
              "div",
              { style: { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" } },
              h("div", { className: "card-title", style: { fontWeight: 900, fontSize: 18 } }, "Arkadaşını Getir Sonuçları"),
              h(
                "button",
                {
                  className: "btn-outline",
                  type: "button",
                  disabled: busy,
                  onClick: async () => {
                    setBusy(true);
                    setErr("");
                    try {
                      const r = await adminListReferrals({ refresh: true });
                      setReferrals((r && r.items) || []);
                    } catch (e2) {
                      setErr((e2 && e2.message) || "Referral yenilenemedi");
                    } finally {
                      setBusy(false);
                    }
                  },
                },
                busy ? "Yenileniyor..." : "Yandex'ten güncelle"
              )
            ),
            h(
              "div",
              { className: "muted", style: { marginTop: 6, fontSize: 12 } },
              "Aktif = davet edilen bu ay en az 1 yolculuk yapmış. Davet edildi = Yandex’te henüz bulunamadı. Bonus: 50 yolculukta 500₺, 100 yolculukta 1000₺."
            ),
            h(
              "div",
              { style: { marginTop: 8, maxHeight: 360, overflow: "auto", border: "1px solid #eee", borderRadius: 10 } },
              !referrals.length
                ? h("div", { className: "muted", style: { padding: 12 } }, "Kayıt yok.")
                : referrals.map((it) =>
                    h(
                      "div",
                      {
                        key: it.id,
                        onClick: () => openReferralDetail(it),
                        style: {
                          padding: "10px 12px",
                          borderBottom: "1px solid #f1f5f9",
                          cursor: "pointer",
                          background: selectedReferralId === it.id ? "#f8fafc" : "#fff",
                        },
                      },
                      h(
                        "div",
                        { style: { display: "flex", justifyContent: "space-between", gap: 8 } },
                        h("div", { style: { fontWeight: 800 } }, `${referralInviteeLabel(it)} • ${referralStatusLabel(it.status)}`),
                        h("div", { className: "muted" }, safeDateLabel(it.created_at))
                      ),
                      h(
                        "div",
                        { className: "muted", style: { marginTop: 4 } },
                        `Davet eden: ${it.inviter_name || "-"} • Yolculuk (bu ay): ${it.orders_count || 0} • Ödül: ${it.reward_amount || 0}₺`
                      )
                    )
                  )
            ),
            selectedReferralId
              ? h(
                  "div",
                  { style: { marginTop: 14, borderTop: "1px solid #eee", paddingTop: 12 } },
                  h("div", { style: { display: "grid", gap: 8 } }, [
                    h("label", { className: "label" }, "Durum"),
                    h(
                      "select",
                      { className: "input", value: refStatus, onChange: (e) => setRefStatus(e.target.value) },
                      h("option", { value: "invited" }, "invited"),
                      h("option", { value: "registered" }, "registered"),
                      h("option", { value: "active" }, "active"),
                      h("option", { value: "reward_pending" }, "reward_pending"),
                      h("option", { value: "reward_paid" }, "reward_paid"),
                      h("option", { value: "rejected" }, "rejected")
                    ),
                    h("label", { className: "label" }, "Yolculuk sayısı"),
                    h("input", {
                      className: "input",
                      type: "number",
                      min: 0,
                      value: refOrders,
                      onChange: (e) => setRefOrders(e.target.value),
                    }),
                    h("label", { className: "label" }, "Ödül tutarı (₺)"),
                    h("input", {
                      className: "input",
                      type: "number",
                      min: 0,
                      step: "0.01",
                      value: refReward,
                      onChange: (e) => setRefReward(e.target.value),
                    }),
                    h(
                      "label",
                      { style: { display: "inline-flex", gap: 8, alignItems: "center", marginTop: 4 } },
                      h("input", { type: "checkbox", checked: refPaid, onChange: (e) => setRefPaid(e.target.checked) }),
                      h("span", null, "Hak manuel verildi")
                    ),
                    h("label", { className: "label" }, "Not"),
                    h("textarea", {
                      className: "input",
                      style: { minHeight: 80, paddingTop: 10 },
                      value: refNote,
                      onChange: (e) => setRefNote(e.target.value),
                    }),
                    h(
                      "button",
                      { className: "btn", type: "button", disabled: busy, onClick: saveReferral, style: { marginTop: 6 } },
                      busy ? "Kaydediliyor..." : "Referral Güncelle"
                    ),
                  ])
                )
              : null
          )
        : null,

      // PAYMENT MANDATES
      section === "mandates"
        ? h(
            "div",
            { className: "card info-card", style: { marginTop: 14 } },
            h(
              "div",
              { style: { display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" } },
              h("div", { className: "card-title", style: { fontWeight: 900, fontSize: 18 } }, "Ödeme Talimatı")
            ),
            h(
              "div",
              { className: "admin-mandate-tabs" },
              mandateSubView !== "excel"
                ? h(
                    "button",
                    {
                      className: "admin-mandate-back",
                      type: "button",
                      onClick: () => {
                        setMandateSubView("excel");
                        setAddErr("");
                        setEditingId("");
                      },
                    },
                    "← Ödeme Talimatı"
                  )
                : null,
              h(
                "button",
                {
                  className: mandateSubView === "schedules" ? "btn" : "btn-outline",
                  type: "button",
                  onClick: () => {
                    setMandateSubView("schedules");
                    setAddErr("");
                  },
                },
                `Otomatik Ödeme Talimatları (${(mandates || []).length})`
              ),
              h(
                "button",
                {
                  className: mandateSubView === "add" ? "btn" : "btn-outline",
                  type: "button",
                  onClick: () => {
                    setMandateSubView("add");
                    setAddErr("");
                    setAddLookup(null);
                    setEditingId("");
                  },
                },
                "Talimat Ekle"
              )
            ),

            mandateSubView === "schedules"
              ? h(
                  "div",
                  { style: { marginTop: 14 } },
                  h(
                    "div",
                    { className: "muted", style: { marginBottom: 10 } },
                    `Kayıtlı otomatik talimatlar: ${filteredMandateSchedules.length}${
                      mandateParkFilter !== "all" ? ` / ${(mandates || []).length}` : ""
                    } · Aktif: ${
                      filteredMandateSchedules.filter((x) => x.enabled && !x.driver_disabled).length
                    } · Sürücü + Excel + admin. Excel 09:00/21:00; bu sayfadan eklenen/düzenlenenler seçilen saatte ödenir (komisyon üzeri bakiye).`
                  ),
                  h(
                    "div",
                    { style: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 } },
                    h(
                      "select",
                      {
                        className: "input",
                        style: { maxWidth: 220, marginBottom: 0 },
                        value: mandateParkFilter,
                        onChange: (e) => setMandateParkFilter(e.target.value),
                      },
                      h("option", { value: "all" }, "Park: Tümü"),
                      ...mandateParkOptions.map((p) => h("option", { key: p.key, value: p.key }, p.label))
                    ),
                    h("input", {
                      className: "input",
                      style: { maxWidth: 320, marginBottom: 0 },
                      placeholder: "Ad veya telefon ile ara...",
                      value: scheduleSearch,
                      onChange: (e) => setScheduleSearch(e.target.value),
                    }),
                    h(
                      "button",
                      {
                        className: "btn",
                        type: "button",
                        disabled: mandateExportBusy || !(mandates || []).length,
                        onClick: async () => {
                          setMandateExportBusy(true);
                          setErr("");
                          try {
                            await adminDownloadMandatesExcel();
                          } catch (e2) {
                            setErr(e2?.message || "Talimat Excel indirilemedi");
                          } finally {
                            setMandateExportBusy(false);
                          }
                        },
                      },
                      mandateExportBusy ? "İndiriliyor..." : "Excel İndir"
                    )
                  ),
                  h(
                    "div",
                    { style: { maxHeight: 560, overflow: "auto", border: "1px solid #eee", borderRadius: 10 } },
                    filteredMandateSchedules.length
                      ? filteredMandateSchedules.map((it) => {
                          const active = it.enabled && !it.driver_disabled;
                          const isWallet = String(it.payout_method) === "wallet";
                          const editing = editingId === it.id;
                          return h(
                            "div",
                            {
                              key: it.id,
                              style: {
                                padding: "10px 12px",
                                borderBottom: "1px solid #f1f5f9",
                                background: active ? "#fff" : "#f8fafc",
                              },
                            },
                            h(
                              "div",
                              { style: { display: "flex", justifyContent: "space-between", gap: 8 } },
                              h("div", { style: { fontWeight: 800 } }, `${it.account_holder || "-"} · ${it.phone || "-"}`),
                              h(
                                "div",
                                {
                                  style: {
                                    fontWeight: 800,
                                    color: active ? "#166534" : it.driver_disabled ? "#b45309" : "#64748b",
                                  },
                                },
                                mandateScheduleStatusLabel(it)
                              )
                            ),
                            h(
                              "div",
                              { className: "muted", style: { marginTop: 4, fontSize: 12 } },
                              `Kaynak: ${mandateSourceLabel(it)} · Şehir: ${it.park_label || it.park_key || "-"} · ${
                                isWallet ? "Sipay cüzdan" : "IBAN"
                              }: ${isWallet ? mandateWalletDisplay(it) || "-" : mandateIbanDisplay(it)} · ${mandateFrequencyLabel(it)} · ${mandateHoursLabel(it)}`
                            ),
                            it.last_result
                              ? h(
                                  "div",
                                  { className: "muted", style: { marginTop: 4, fontSize: 12 } },
                                  `Son: ${it.last_result}${it.last_error ? ` · ${it.last_error}` : ""}`
                                )
                              : null,
                            editing
                              ? h(
                                  "div",
                                  { className: "admin-mandate-edit-box" },
                                  h("div", { className: "label", style: { marginBottom: 6 } }, "Ödeme kanalı"),
                                  renderMandateMethodToggle(
                                    editMethod,
                                    (method) => {
                                      setEditMethod(method);
                                      if (method === "iban" && !String(editIban || "").trim()) {
                                        setEditIban(filledMandateIban(it));
                                      }
                                    },
                                    busy
                                  ),
                                  editMethod === "iban"
                                    ? h(
                                        "div",
                                        { style: { marginTop: 8, maxWidth: 360 } },
                                        h("input", {
                                          className: "input",
                                          placeholder: "IBAN",
                                          value: editIban,
                                          onChange: (e) => setEditIban(e.target.value),
                                        }),
                                        h("input", {
                                          className: "input",
                                          style: { marginTop: 8 },
                                          placeholder: "Ad Soyad",
                                          value: editHolder,
                                          onChange: (e) => setEditHolder(e.target.value),
                                        }),
                                        mandateIbanFillHint(it)
                                          ? h(
                                              "div",
                                              {
                                                className: "muted",
                                                style: {
                                                  marginTop: 6,
                                                  fontSize: 12,
                                                  color:
                                                    it.iban_lookup_conflict || (!it.iban && !it.iban_lookup)
                                                      ? "#b45309"
                                                      : "#64748b",
                                                },
                                              },
                                              mandateIbanFillHint(it)
                                            )
                                          : null
                                      )
                                    : h(
                                        "div",
                                        { style: { marginTop: 8 } },
                                        h(
                                          "div",
                                          { className: "muted", style: { fontSize: 12 } },
                                          `Sipay cüzdan: ${mandateWalletDisplay(it) || "numara kayıtlı değil"}`
                                        ),
                                        mandateWalletFillHint(it)
                                          ? h(
                                              "div",
                                              {
                                                className: "muted",
                                                style: {
                                                  marginTop: 6,
                                                  fontSize: 12,
                                                  color: mandateWalletDisplay(it) ? "#64748b" : "#b45309",
                                                },
                                              },
                                              mandateWalletFillHint(it)
                                            )
                                          : null
                                      ),
                                  renderMandateHourFields({
                                    hour1: editHour1,
                                    setHour1: setEditHour1,
                                    hour2On: editHour2On,
                                    setHour2On: setEditHour2On,
                                    hour2: editHour2,
                                    setHour2: setEditHour2,
                                    disabled: busy,
                                  }),
                                  h(
                                    "div",
                                    { className: "admin-mandate-item-actions" },
                                    h(
                                      "button",
                                      {
                                        className: "btn",
                                        type: "button",
                                        disabled: busy,
                                        onClick: () => saveEditMandate(it),
                                      },
                                      busy ? "Kaydediliyor..." : "Kaydet"
                                    ),
                                    h(
                                      "button",
                                      {
                                        className: "btn-outline",
                                        type: "button",
                                        disabled: busy,
                                        onClick: () => setEditingId(""),
                                      },
                                      "Vazgeç"
                                    )
                                  )
                                )
                              : h(
                                  "div",
                                  { className: "admin-mandate-item-actions" },
                                  !active && !it.driver_disabled
                                    ? h(
                                        "button",
                                        {
                                          className: "btn-outline",
                                          type: "button",
                                          disabled: busy,
                                          onClick: () => toggleMandate(it),
                                        },
                                        "Aktif Et"
                                      )
                                    : null,
                                  h(
                                    "button",
                                    {
                                      className: "btn-outline",
                                      type: "button",
                                      disabled: busy,
                                      onClick: () => startEditMandate(it),
                                    },
                                    "Düzenle"
                                  ),
                                  h(
                                    "button",
                                    {
                                      className: "btn-outline admin-mandate-remove-btn",
                                      type: "button",
                                      disabled: busy,
                                      onClick: () => removeMandate(it),
                                    },
                                    "Talimatı Kaldır"
                                  )
                                )
                          );
                        })
                      : h(
                          "div",
                          { className: "muted", style: { padding: 12 } },
                          mandateParkFilter !== "all" || String(scheduleSearch || "").trim()
                            ? "Bu filtrede talimat yok."
                            : "Kayıtlı talimat yok."
                        )
                  )
                )
              : null,

            mandateSubView === "add"
              ? h(
                  "div",
                  { style: { marginTop: 14 } },
                  h(
                    "div",
                    { className: "muted", style: { marginBottom: 10 } },
                    "Telefon numarası ile sürücüyü bulun. Kanal, hesap ve periyot sürücü talimat formuyla aynı: günlük, 2 günde bir, 3 günde bir, haftalık veya aylık."
                  ),
                  h(
                    "div",
                    { style: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" } },
                    h("input", {
                      className: "input",
                      style: { maxWidth: 260 },
                      placeholder: "+905xxxxxxxxx",
                      value: addPhone,
                      onChange: (e) => setAddPhone(e.target.value),
                    }),
                    h(
                      "button",
                      { className: "btn-outline", type: "button", disabled: addBusy, onClick: lookupMandateDriver },
                      addBusy && !addLookup ? "Aranıyor..." : "Sürücü Bul"
                    )
                  ),
                  addErr ? h("div", { style: { marginTop: 8, color: "#b91c1c", fontWeight: 700 } }, addErr) : null,
                  addLookup
                    ? h(
                        "div",
                        {
                          style: {
                            marginTop: 12,
                            padding: 12,
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                            background: "#f8fafc",
                          },
                        },
                        h("div", { style: { fontWeight: 900, fontSize: 16 } }, addLookup.account_holder || "-"),
                        h(
                          "div",
                          { className: "muted", style: { marginTop: 6, fontSize: 13 } },
                          `Telefon: ${addLookup.phone || "-"} · Şehir: ${addLookup.park_label || addLookup.park_key || "-"} · Yandex Id: ${
                            addLookup.driver_profile_id || "-"
                          }`
                        ),
                        addLookup.payout_reason_detail
                          ? h("div", { className: "muted", style: { marginTop: 6, fontSize: 12 } }, addLookup.payout_reason_detail)
                          : null,
                        addLookup.existing_mandate
                          ? h(
                              "div",
                              { style: { marginTop: 8, color: "#b45309", fontWeight: 700, fontSize: 13 } },
                              addLookup.has_active_mandate
                                ? "Bu sürücünün zaten aktif talimatı var. Listeden düzenleyin."
                                : `Mevcut talimat: ${mandateScheduleStatusLabel(addLookup.existing_mandate)}`
                            )
                          : null,
                        h("div", { className: "label", style: { marginTop: 12 } }, "Ödeme kanalı"),
                        renderMandateMethodToggle(addMethod, setAddMethod, addBusy || addLookup.has_active_mandate),
                        addMethod === "wallet"
                          ? h(
                              "div",
                              { className: "muted", style: { marginTop: 8, fontSize: 12 } },
                              "Sipay cüzdan, kaydederken telefonla aranır."
                            )
                          : h(
                              "div",
                              { style: { marginTop: 10, display: "grid", gap: 8, maxWidth: 420 } },
                              h("label", { className: "label" }, "IBAN"),
                              h("input", {
                                className: "input",
                                placeholder: "TRxxxxxxxxxxxxxxxxxxxxxxxx",
                                value: addIban,
                                onChange: (e) => setAddIban(e.target.value),
                              }),
                              mandateIbanFillHint(addLookup)
                                ? h(
                                    "div",
                                    { className: "muted", style: { fontSize: 12, color: "#64748b" } },
                                    mandateIbanFillHint(addLookup)
                                  )
                                : null
                            ),
                        h("label", { className: "label", style: { marginTop: 8 } }, "Hesap sahibi adı"),
                        h("input", {
                          className: "input",
                          style: { maxWidth: 420 },
                          placeholder: "Ad Soyad",
                          value: addHolder,
                          onChange: (e) => setAddHolder(e.target.value),
                        }),
                        renderMandatePeriodFields({
                          frequency: addFrequency,
                          setFrequency: setAddFrequency,
                          weekday: addWeekday,
                          setWeekday: setAddWeekday,
                          dayOfMonth: addDayOfMonth,
                          setDayOfMonth: setAddDayOfMonth,
                          disabled: addBusy || addLookup.has_active_mandate,
                        }),
                        renderMandateHourFields({
                          hour1: addHour1,
                          setHour1: setAddHour1,
                          hour2On: addHour2On,
                          setHour2On: setAddHour2On,
                          hour2: addHour2,
                          setHour2: setAddHour2,
                          disabled: addBusy || addLookup.has_active_mandate,
                        }),
                        h(
                          "button",
                          {
                            className: "btn",
                            type: "button",
                            style: { marginTop: 12, maxWidth: 240 },
                            disabled: addBusy || addLookup.has_active_mandate,
                            onClick: submitAddMandate,
                          },
                          addBusy ? "Ekleniyor..." : "Otomatik Ödeme Talimatı Ekle"
                        )
                      )
                    : null
                )
              : null,

            mandateSubView === "excel"
              ? h(
                  React.Fragment,
                  null,
            h(
              "div",
              { className: "muted", style: { marginTop: 6 } },
              "Excel'de Id (Yandex sürücü id), Telefon, Ad Soyad, IBAN olmalı. Ödeme Faturamatik İşyerimPOS moneyTransfer ile IBAN'a gider. Aynı anda 4 kişiye kadar gönderilir; her kişide API başarılı (IsDone) dönene kadar o kişi için beklenir. Poll kapalıyken bu yükleme 09:00+21:00 kuyruğuna yazmaz."
            ),
            h(
              "div",
              {
                style: {
                  marginTop: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: "#fff7ed",
                  border: "1px solid #fdba74",
                  fontWeight: 700,
                  fontSize: 13,
                  lineHeight: 1.45,
                },
              },
              "Not: Excel yüklenince ödeme hemen başlar. Aynı anda en fazla 4 kişi İşyerimPOS'a gider; her birinde IsDone beklenir. Poll (09:00+21:00) şu an kapalıdır; test Excel’i kuyruğa almaz. Başarısızlar alt listede nedeniyle durur; “Başarısız excel indir” Toplu IBAN sonuç formatındadır. Sürücü panelden kapatanlar sonraki otomatik kuyruğa alınmaz."
            ),
            h(
              "div",
              { style: { marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" } },
              h("input", {
                className: "input",
                type: "file",
                accept: ".xlsx,.csv",
                style: { maxWidth: 280 },
                onChange: (e) => setMandateFile((e.target.files && e.target.files[0]) || null),
              }),
              h(
                "button",
                { className: "btn", type: "button", disabled: busy || !mandateFile, onClick: uploadMandates },
                busy && !mandateBatchId ? "Yükleniyor..." : "Excel Yükle"
              ),
              h(
                "button",
                {
                  className: "btn-outline",
                  type: "button",
                  disabled: busy,
                  onClick: () => adminDownloadMandateTemplate().catch((e2) => setErr(e2.message || "Şablon indirilemedi")),
                },
                "Şablon İndir"
              ),
              h(
                "button",
                {
                  className: "btn-outline admin-mandate-remove-btn",
                  type: "button",
                  disabled: busy,
                  onClick: clearAllMandates,
                },
                "Listeyi Temizle"
              )
            ),
            mandateFile ? h("div", { className: "muted", style: { marginTop: 6 } }, `Seçili: ${mandateFile.name}`) : null,
            mandateMsg ? h("div", { style: { marginTop: 8, color: "#166534", fontWeight: 700 } }, mandateMsg) : null,
            mandateProgress
              ? h(
                  "div",
                  {
                    style: {
                      marginTop: 12,
                      padding: 12,
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      background: "#f8fafc",
                    },
                  },
                  h(
                    "div",
                    { style: { display: "flex", justifyContent: "space-between", gap: 8, fontWeight: 800, alignItems: "center" } },
                    h("div", null, `İlerleme: ${mandateProgress.done || 0}/${mandateProgress.total || 0}`),
                    h("div", null, `%${mandateProgress.percent || 0}`)
                  ),
                  mandateBatchId &&
                    !["done", "error", "cancelled"].includes(String(mandateProgress.status || ""))
                    ? h(
                        "div",
                        { style: { marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" } },
                        h(
                          "button",
                          {
                            className: "btn-outline",
                            type: "button",
                            onClick: () => controlMandateUpload(mandatePaused ? "resume" : "pause"),
                          },
                          mandatePaused ? "Devam Et" : "Durdur"
                        ),
                        h(
                          "button",
                          {
                            className: "btn-outline",
                            type: "button",
                            style: { color: "#b91c1c" },
                            onClick: () => controlMandateUpload("cancel"),
                          },
                          "İptal"
                        ),
                        mandatePaused
                          ? h("span", { style: { fontWeight: 800, color: "#b45309", alignSelf: "center" } }, "DURAKLATILDI")
                          : null
                      )
                    : null,
                  h(
                    "div",
                    {
                      style: {
                        marginTop: 8,
                        height: 12,
                        borderRadius: 999,
                        background: "#e5e7eb",
                        overflow: "hidden",
                      },
                    },
                    h("div", {
                      style: {
                        width: `${Math.min(100, Math.max(0, Number(mandateProgress.percent || 0)))}%`,
                        height: "100%",
                        background:
                          mandateProgress.status === "error"
                            ? "#dc2626"
                            : mandateProgress.status === "paused"
                            ? "#f59e0b"
                            : "#16a34a",
                        transition: "width 0.25s ease",
                      },
                    })
                  ),
                  h(
                    "div",
                    { className: "muted", style: { marginTop: 8, fontSize: 13 } },
                    `Başarılı (IsDone): ${mandateProgress.paid || mandateProgress.ok || 0} · Başarısız: ${
                      mandateProgress.failed || 0
                    } · Aynı anda: ${mandateProgress.inflight || 0}/${mandateProgress.concurrency || 4}${
                      mandateProgress.message ? ` · ${mandateProgress.message}` : ""
                    }`
                  ),
                  mandateProgress.current_row || mandateProgress.current_phone
                    ? h(
                        "div",
                        { style: { marginTop: 6, fontSize: 13, fontWeight: 700 } },
                        `Şimdi: satır ${mandateProgress.current_row || "-"} · ${
                          mandateProgress.current_holder || "-"
                        } · ${mandateProgress.current_phone || "-"} · ${mandateUploadStatusLabel(
                          mandateProgress.current_status
                        )}`
                      )
                    : null,
                  String(mandateProgress.current_status || "") === "api_waiting"
                    ? h(
                        "div",
                        { style: { marginTop: 4, color: "#1d4ed8", fontSize: 12, fontWeight: 700 } },
                        "API yanıtı bekleniyor — bu kişi(ler) için IsDone gelmeden ödeme tamamlanmış sayılmaz."
                      )
                    : null,
                  mandateProgress.current_error
                    ? h(
                        "div",
                        { style: { marginTop: 4, color: "#b91c1c", fontSize: 12, fontWeight: 700 } },
                        mandateProgress.current_error
                      )
                    : null
                )
              : null,
            h(
              "div",
              { style: { marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" } },
              h(
                "select",
                {
                  className: "input",
                  style: { maxWidth: 220 },
                  value: mandateFilter,
                  onChange: (e) => setMandateFilter(e.target.value),
                },
                h("option", { value: "all" }, `Tümü (${mandateLogStats.total})`),
                h("option", { value: "ok" }, `Başarılı (${mandateLogStats.ok})`),
                h("option", { value: "failed" }, `Başarısız (${mandateLogStats.failed})`)
              ),
              h(
                "button",
                {
                  className: "btn-outline",
                  type: "button",
                  disabled: busy || !mandateBatchId || !mandateLogStats.failed,
                  onClick: downloadFailedMandateExcel,
                },
                "Başarısız excel indir"
              )
            ),
            h(
              "div",
              { style: { marginTop: 14, maxHeight: 480, overflow: "auto", border: "1px solid #eee", borderRadius: 10 } },
              filteredMandateLogs.length
                ? filteredMandateLogs.map((it) => {
                    const excelOk = String(it.status || "") === "ok";
                    const paid = mandateMoneySent(it);
                    const failReason = it.pay_error || it.error || "";
                    const mandate = excelOk ? (mandates || []).find((m) => m.id === it.mandate_id) : null;
                    const inPoll = Boolean(
                      mandate && mandate.enabled && !mandate.skip_auto_poll && !mandate.auto_send_paused
                    );
                    const recordKind = String(it.record_kind || "");
                    const autoLabel = recordKind || (inPoll ? "09:00+21:00 OTO" : "Excel anlık");
                    const amountLabel =
                      it.pay_amount != null && it.pay_amount !== ""
                        ? `${Number(it.pay_amount).toFixed(2)} TL`
                        : "";
                    return h(
                      "div",
                      {
                        key: it.id,
                        style: {
                          padding: "10px 12px",
                          borderBottom: "1px solid #f1f5f9",
                          background: paid ? "#fff" : "#fef2f2",
                        },
                      },
                      h(
                        "div",
                        { style: { display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" } },
                        h(
                          "div",
                          { style: { fontWeight: 800 } },
                          `${it.account_holder || "-"} • ${it.phone || it.raw_phone || "-"}`
                        ),
                        h(
                          "div",
                          {
                            style: {
                              fontWeight: 800,
                              color: paid ? "#166534" : "#b91c1c",
                            },
                          },
                          paid ? "BAŞARILI" : "BAŞARISIZ"
                        )
                      ),
                      it.excel_row
                        ? h("div", { className: "muted", style: { marginTop: 2, fontSize: 12 } }, `Excel satır: ${it.excel_row}`)
                        : null,
                      recordKind
                        ? h(
                            "div",
                            { style: { marginTop: 4, fontWeight: 800, fontSize: 13, color: paid ? "#1d4ed8" : "#9a3412" } },
                            `${recordKind}${amountLabel ? ` · ${amountLabel}` : ""}`
                          )
                        : null,
                      !paid && failReason
                        ? h(
                            "div",
                            { style: { marginTop: 6, color: "#b91c1c", fontWeight: 700, fontSize: 13 } },
                            failReason
                          )
                        : null,
                      excelOk
                        ? h(
                            "div",
                            {
                              style: {
                                marginTop: 6,
                                fontWeight: 900,
                                fontSize: 15,
                                color: paid ? "#1d4ed8" : "#b91c1c",
                              },
                            },
                            (() => {
                              const ps = String(it.pay_status || "");
                              const channel = String(it.payout_method) === "wallet" ? "Cüzdan" : "IBAN";
                              let payLabel = channel;
                              if (["paid", "completed", "success"].includes(ps)) payLabel = `${channel} · ÖDENDİ`;
                              else if (ps === "skipped") payLabel = `${channel} · BAKİYE YOK`;
                              else if (["payout_pending", "payout_sending", "awaiting_payout", "processing", "approved"].includes(ps))
                                payLabel = `${channel} · İŞLENİYOR`;
                              else payLabel = `${channel} · ÖDEME HATA`;
                              return `${payLabel} · ${autoLabel}${amountLabel ? ` · ${amountLabel}` : ""}`;
                            })()
                          )
                        : null,
                      excelOk && it.payout_reason_detail
                        ? h(
                            "div",
                            {
                              style: {
                                marginTop: 4,
                                fontSize: 12,
                                fontWeight: 600,
                                color: String(it.payout_method) === "wallet" ? "#166534" : "#9a3412",
                              },
                            },
                            it.payout_reason_detail
                          )
                        : null,
                      excelOk && it.pay_error
                        ? h(
                            "div",
                            { style: { marginTop: 4, color: "#b45309", fontSize: 12, fontWeight: 700 } },
                            `Ödeme: ${it.pay_error}`
                          )
                        : null,
                      h(
                        "div",
                        { className: "muted", style: { marginTop: 4, fontSize: 12 } },
                        `Şehir: ${it.park_label || it.park_key || "-"} • IBAN: ${it.iban_masked || it.raw_iban || "-"} • KYC: ${
                          it.kyc_level != null ? it.kyc_level : "-"
                        } • ${safeDateLabel(it.created_at)}`
                      ),
                      excelOk && mandate
                        ? h(
                            "div",
                            { className: "admin-mandate-item-actions" },
                            !mandate.enabled && !mandate.driver_disabled
                              ? h(
                                  "button",
                                  {
                                    className: "btn-outline",
                                    type: "button",
                                    disabled: busy,
                                    onClick: () => toggleMandate(mandate),
                                  },
                                  "Aktif Et"
                                )
                              : null,
                            h(
                              "button",
                              {
                                className: "btn-outline admin-mandate-remove-btn",
                                type: "button",
                                disabled: busy,
                                onClick: () => removeMandate(mandate),
                              },
                              "Talimatı Kaldır"
                            )
                          )
                        : null
                    );
                  })
                : h("div", { className: "muted", style: { padding: 12 } }, "Kayıt yok.")
            )
                )
              : null
          )
        : null,

      // WITHDRAWALS
      section === "withdrawals"
        ? h(
            "div",
            { className: "card info-card admin-withdrawals", style: { marginTop: 14 } },
            h(
              "div",
              {
                className: "admin-payout-balance-row",
                style: {
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 10,
                  marginBottom: 12,
                },
              },
              h(
                "div",
                {
                  className: "card admin-kpi-card",
                  style: {
                    padding: "12px 14px",
                    border: "1px solid #bbf7d0",
                    background: "linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)",
                  },
                },
                [
                  h("div", { className: "muted", style: { fontSize: 12, fontWeight: 600 } }, "Sipay Kullanılabilir Bakiye"),
                  h(
                    "div",
                    {
                      style: {
                        fontWeight: 900,
                        fontSize: 22,
                        marginTop: 4,
                        color: "#15803d",
                        minHeight: 28,
                      },
                    },
                    payoutBalancesBusy && !payoutBalances
                      ? "Yükleniyor..."
                      : payoutBalances?.sipay?.ok
                      ? formatMoneyTry(payoutBalances.sipay.balance)
                      : "—"
                  ),
                  payoutBalances?.sipay?.ok && payoutBalances?.sipay?.total_balance != null
                    ? h(
                        "div",
                        { className: "muted", style: { fontSize: 11, marginTop: 4 } },
                        `Toplam: ${formatMoneyTry(payoutBalances.sipay.total_balance)}`
                      )
                    : null,
                  payoutBalances?.sipay?.wallet_number_masked
                    ? h(
                        "div",
                        { className: "muted", style: { fontSize: 11, marginTop: 4 } },
                        `Cüzdan: ${payoutBalances.sipay.wallet_number_masked}`
                      )
                    : null,
                  payoutBalances?.sipay?.error && !payoutBalances?.sipay?.ok
                    ? h("div", { style: { fontSize: 11, marginTop: 4, color: "#b91c1c" } }, payoutBalances.sipay.error)
                    : null,
                ]
              ),
              h(
                "div",
                {
                  className: "card admin-kpi-card",
                  style: {
                    padding: "12px 14px",
                    border: "1px solid #bfdbfe",
                    background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)",
                  },
                },
                [
                  h("div", { className: "muted", style: { fontSize: 12, fontWeight: 600 } }, "Sipay Cüzdan Çıkışı"),
                  h(
                    "div",
                    { style: { fontWeight: 900, fontSize: 22, marginTop: 4, color: "#1d4ed8", minHeight: 28 } },
                    formatMoneyTry(channelOutflow.sipayNet)
                  ),
                  h(
                    "div",
                    { className: "muted", style: { fontSize: 11, marginTop: 4 } },
                    `${outflowRangeCaption(wdRangePreset)} · ${channelOutflow.sipayCount} işlem`
                  ),
                ]
              ),
              h(
                "div",
                {
                  className: "card admin-kpi-card",
                  style: {
                    padding: "12px 14px",
                    border: "1px solid #fde68a",
                    background: "linear-gradient(135deg, #fffbeb 0%, #ffffff 100%)",
                  },
                },
                [
                  h("div", { className: "muted", style: { fontSize: 12, fontWeight: 600 } }, "VakıfBank Çıkışı"),
                  h(
                    "div",
                    { style: { fontWeight: 900, fontSize: 22, marginTop: 4, color: "#b45309", minHeight: 28 } },
                    formatMoneyTry(channelOutflow.vakifNet)
                  ),
                  h(
                    "div",
                    { className: "muted", style: { fontSize: 11, marginTop: 4 } },
                    `${outflowRangeCaption(wdRangePreset)} · ${channelOutflow.vakifCount} işlem`
                  ),
                ]
              )
            ),
            h(
              "div",
              { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 } },
              h(
                "div",
                null,
                h("div", { className: "card-title", style: { fontWeight: 900, fontSize: 18 } }, "Para Çekim Talepleri"),
                h(
                      "div",
                      { className: "muted", style: { fontSize: 12, marginTop: 4 } },
                      payoutMaintenance
                        ? `Bakımda${
                            velixOpen
                              ? " · Velix açık (otomatik talimat dahil)"
                              : " · Velix bakımda"
                          }${
                            (maintenanceExemptParks || []).filter((k) => k !== "velix_go").length
                              ? ` · açık: ${(maintenanceExemptParks || [])
                                  .filter((k) => k !== "velix_go")
                                  .map((k) => parksLabelMap[k] || k)
                                  .join(", ")}`
                              : ""
                          }`
                        : `Cüzdan açık · IBAN ${
                            driverIbanEnabled ? "tüm parklarda açık" : "kapalı"
                          } · Otomatik talimat ${
                            autoWithdrawPollEnabled ? "açık" : "bakımda"
                          } · Velix ${velixOpen ? "açık" : "bakımda"}`
                    )
              ),
              h(
                "div",
                { style: { display: "flex", gap: 8, flexWrap: "wrap" } },
                h(
                  "button",
                  {
                    type: "button",
                    disabled: maintenanceBusy,
                    style: {
                      padding: "6px 16px",
                      borderRadius: 8,
                      border: "none",
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: maintenanceBusy ? "wait" : "pointer",
                      color: "#fff",
                      background: velixOpen ? "#b45309" : "#0f766e",
                    },
                    onClick: toggleVelixMaintenance,
                  },
                  maintenanceBusy ? "İşleniyor..." : velixOpen ? "Velix Bakıma Al" : "Velix Bakımdan Çıkar"
                ),
                h(
                  "button",
                  {
                    type: "button",
                    disabled: maintenanceBusy,
                    style: {
                      padding: "6px 16px",
                      borderRadius: 8,
                      border: "none",
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: maintenanceBusy ? "wait" : "pointer",
                      color: "#fff",
                      background: payoutMaintenance ? "#16a34a" : "#dc2626",
                    },
                    onClick: toggleMaintenance,
                  },
                  maintenanceBusy
                    ? "İşleniyor..."
                    : payoutMaintenance
                    ? "Bakımdan Çıkar"
                    : "Bakıma Al"
                )
              )
            ),
            h(
              "div",
              { className: "admin-kpi-grid", style: { marginTop: 10, display: "grid", gridTemplateColumns: "repeat(3, minmax(170px, 1fr))", gap: 8 } },
              h("div", { className: "card admin-kpi-card", style: { padding: 10, border: "1px solid #e5e7eb" } }, [
                h("div", { className: "muted", style: { fontSize: 12 } }, "Net Tutar (onaylanan)"),
                h("div", { style: { fontWeight: 900, marginTop: 2, color: "#166534" } }, `${(withdrawalReport.totalApprovedNet || 0).toFixed(2)}₺`),
              ]),
              h("div", { className: "card admin-kpi-card", style: { padding: 10, border: "1px solid #e5e7eb" } }, [
                h("div", { className: "muted", style: { fontSize: 12 } }, "Ödenen Tutar (net)"),
                h("div", { style: { fontWeight: 900, marginTop: 2, color: "#166534" } }, `${(withdrawalReport.todayPaid || 0).toFixed(2)}₺`),
              ]),
              h("div", { className: "card admin-kpi-card", style: { padding: 10, border: "1px solid #e5e7eb" } }, [
                h("div", { className: "muted", style: { fontSize: 12 } }, "Hizmet Bedeli"),
                h("div", { style: { fontWeight: 900, marginTop: 2, color: "#92400e" } }, `${(withdrawalReport.totalCommission || 0).toFixed(2)}₺`),
              ])
            ),
            h(
              "div",
              { className: "admin-filter-bar", style: { marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" } },
              h(
                "select",
                { className: "input", style: { maxWidth: 220 }, value: wdCityFilter, onChange: (e) => setWdCityFilter(e.target.value) },
                h("option", { value: "all" }, "Park: Tümü"),
                ...withdrawalCities.map((k) => h("option", { key: k, value: k }, parksLabelMap[k] || k))
              ),
              h(
                "select",
                {
                  className: "input",
                  style: { maxWidth: 170 },
                  value: wdRangePreset,
                  onChange: (e) => {
                    const v = e.target.value;
                    setWdRangePreset(v);
                    const applied = withdrawalDatePresetRange(v);
                    if (applied) {
                      setWdDateFrom(applied.from);
                      setWdDateTo(applied.to);
                    }
                  },
                },
                h("option", { value: "today" }, "Tarih: Bugün"),
                h("option", { value: "month" }, "Tarih: Bu Ay"),
                h("option", { value: "custom" }, "Tarih: Aralık"),
                h("option", { value: "all" }, "Tarih: Tümü")
              ),
              h(
                "select",
                {
                  className: "input",
                  style: { maxWidth: 200 },
                  value: wdMethodFilter,
                  onChange: (e) => setWdMethodFilter(e.target.value),
                },
                h("option", { value: "all" }, "Tür: Tümü"),
                h("option", { value: "iban" }, "Tür: IBAN"),
                h("option", { value: "wallet" }, "Tür: Sipay Cüzdan")
              ),
              h(
                "select",
                { className: "input", style: { maxWidth: 300 }, value: wdFilter, onChange: (e) => setWdFilter(e.target.value) },
                h("option", { value: "all" }, `Durum: Tümü (${withdrawalStats.total})`),
                h("option", { value: "paid" }, `Durum: Başarılı (${withdrawalStats.paid})`),
                h("option", { value: "failed" }, `Durum: Başarısız (${withdrawalStats.failed})`),
                h(
                  "option",
                  { value: "payout_pending" },
                  `Durum: IBAN banka onayı bekleniyor (${withdrawalStats.bankPending})`
                ),
                h(
                  "option",
                  { value: "sending" },
                  `Durum: Gönderiliyor (${withdrawalStats.sending || 0})`
                )
              ),
              h(
                "label",
                { style: { display: "flex", flexDirection: "column", gap: 2, fontSize: 11, color: "#6b7280" } },
                "Başlangıç",
                h("input", {
                  className: "input",
                  type: "date",
                  style: { maxWidth: 160 },
                  value: wdDateFrom,
                  disabled: wdRangePreset === "all",
                  onChange: (e) => {
                    setWdDateFrom(e.target.value);
                    setWdRangePreset("custom");
                  },
                })
              ),
              h(
                "label",
                { style: { display: "flex", flexDirection: "column", gap: 2, fontSize: 11, color: "#6b7280" } },
                "Bitiş",
                h("input", {
                  className: "input",
                  type: "date",
                  style: { maxWidth: 160 },
                  value: wdDateTo,
                  disabled: wdRangePreset === "all",
                  onChange: (e) => {
                    setWdDateTo(e.target.value);
                    setWdRangePreset("custom");
                  },
                })
              ),
              h("input", {
                className: "input",
                style: { maxWidth: 170 },
                placeholder: "Telefon",
                value: wdPhoneQuery,
                onChange: (e) => setWdPhoneQuery(e.target.value),
              }),
              h("input", {
                className: "input",
                style: { maxWidth: 180 },
                placeholder: "İsim Soyisim",
                value: wdNameQuery,
                onChange: (e) => setWdNameQuery(e.target.value),
              }),
              h(
                "button",
                {
                  className: "btn",
                  type: "button",
                  disabled: busy,
                  onClick: async () => {
                    setBusy(true);
                    setErr("");
                    try {
                      await adminDownloadWithdrawalsExcel({
                        park_key: wdCityFilter !== "all" ? wdCityFilter : "",
                        date_from: wdRangePreset !== "all" ? wdDateFrom : "",
                        date_to: wdRangePreset !== "all" ? wdDateTo : "",
                        phone: wdPhoneQuery,
                        name: wdNameQuery,
                        status: wdFilter !== "all" ? wdFilter : "",
                        payout_method: wdMethodFilter !== "all" ? wdMethodFilter : "",
                      });
                    } catch (e) {
                      setErr((e && e.message) || "Excel indirilemedi");
                    } finally {
                      setBusy(false);
                    }
                  },
                },
                busy ? "İndiriliyor..." : "Excel'e İndir"
              ),
              h(
                "button",
                {
                  className: "btn-outline",
                  type: "button",
                  onClick: () => {
                    setWdCityFilter("all");
                    setWdRangePreset("today");
                    const today = localDateKey();
                    setWdDateFrom(today);
                    setWdDateTo(today);
                    setWdPhoneQuery("");
                    setWdNameQuery("");
                    setWdMethodFilter("all");
                    setWdFilter("all");
                  },
                },
                "Filtreleri Temizle"
              )
            ),
            h(
              "div",
              { className: "muted", style: { marginTop: 6, fontSize: 12 } },
              `Kapsam: ${wdCityFilter === "all" ? "Tüm parklar" : parksLabelMap[wdCityFilter] || wdCityFilter} • ${
                wdRangePreset === "all"
                  ? "Tüm zaman"
                  : wdRangePreset === "today"
                  ? `Bugün (${wdDateFrom || localDateKey()})`
                  : wdRangePreset === "month"
                  ? `Bu ay (${wdDateFrom || monthStartDateKey()} – ${wdDateTo || localDateKey()})`
                  : `${wdDateFrom || "?"} – ${wdDateTo || "?"}`
              } • ${
                wdMethodFilter === "iban"
                  ? "IBAN"
                  : wdMethodFilter === "wallet"
                  ? "Sipay Cüzdan"
                  : "Tüm türler"
              } • ${
                wdFilter === "paid"
                  ? "Başarılı"
                  : wdFilter === "failed"
                  ? "Başarısız"
                  : wdFilter === "payout_pending"
                  ? "IBAN banka onayı bekleniyor"
                  : wdFilter === "sending"
                  ? "Gönderiliyor"
                  : "Tüm durumlar"
              } • Gösterilen: ${filteredWithdrawals.length} kayıt`
            ),
            h(
              "div",
              { className: "admin-city-summary", style: { marginTop: 10, border: "1px solid #eee", borderRadius: 10, overflow: "hidden" } },
              h(
                "div",
                { style: { display: "grid", gridTemplateColumns: "1.2fr 0.8fr 1fr 1fr", padding: "8px 10px", fontSize: 12, color: "#6b7280", fontWeight: 800, background: "#f8fafc" } },
                h("div", null, "Park"),
                h("div", { style: { textAlign: "right" } }, "Talep Adedi"),
                h("div", { style: { textAlign: "right" } }, "Talep Toplam (brüt)"),
                h("div", { style: { textAlign: "right" } }, "Ödenen Toplam (net)")
              ),
              ...(withdrawalReport.cityRows || []).slice(0, 8).map((r) =>
                h(
                  "div",
                  { key: r.cityKey, style: { display: "grid", gridTemplateColumns: "1.2fr 0.8fr 1fr 1fr", padding: "8px 10px", borderTop: "1px solid #f1f5f9", fontSize: 13 } },
                  h("div", { style: { fontWeight: 700 } }, r.cityLabel),
                  h("div", { style: { textAlign: "right" } }, String(r.reqCount || 0)),
                  h("div", { style: { textAlign: "right" } }, `${Number(r.reqTotal || 0).toFixed(2)}₺`),
                  h("div", { style: { textAlign: "right", color: "#166534", fontWeight: 700 } }, `${Number(r.paidTotal || 0).toFixed(2)}₺`)
                )
              )
            ),
            h(
              "div",
              { className: "admin-withdraw-list", style: { marginTop: 8, maxHeight: 360, overflow: "auto", border: "1px solid #eee", borderRadius: 10 } },
              filteredWithdrawals.length
                ? filteredWithdrawals.map((it, idx) => {
                    const banner = withdrawalSentBanner(it);
                    const bannerColor =
                      banner.kind === "ok"
                        ? "#166534"
                        : banner.kind === "failed"
                        ? "#b91c1c"
                        : banner.kind === "refunded"
                        ? "#1d4ed8"
                        : "#92400e";
                    return h(
                      "div",
                      {
                        key: `${it.id || "wd"}-${idx}`,
                        onClick: () => openWithdrawalDetail(it),
                        style: {
                          padding: "10px 12px",
                          borderBottom: "1px solid #f1f5f9",
                          cursor: "pointer",
                          background: selectedWithdrawalId === it.id ? "#f8fafc" : "#fff",
                        },
                      },
                      h(
                        "div",
                        { style: { display: "flex", justifyContent: "space-between", gap: 8 } },
                        h(
                          "div",
                          { style: { fontWeight: 800 } },
                          `${withdrawalDriverName(it)} • ${it.phone || "-"}`
                        ),
                        h("div", { className: "muted" }, safeDateLabel(it.created_at))
                      ),
                      h(
                        "div",
                        {
                          style: {
                            marginTop: 6,
                            fontWeight: 900,
                            fontSize: 16,
                            letterSpacing: 0.2,
                            color: bannerColor,
                          },
                        },
                        banner.title
                      ),
                      (banner.kind === "failed" || banner.kind === "refunded") && banner.detail
                        ? h(
                            "div",
                            {
                              style: {
                                marginTop: 4,
                                color: banner.kind === "refunded" ? "#1d4ed8" : "#b91c1c",
                                fontSize: 13,
                                fontWeight: 600,
                              },
                            },
                            banner.detail
                          )
                        : null,
                      h(
                        "div",
                        { className: "muted", style: { marginTop: 4, fontSize: 12 } },
                        `Park: ${parksLabelMap[it.park_key] || it.park_key || "-"} • Tür: ${
                          it.payout_method_label || payoutMethodLabel(it.payout_method)
                        } • ${it.source === "admin_mandate" ? "Otomatik Talimat" : it.source === "auto_schedule" ? "Otomatik Talimat" : "Manuel Çekim"}`
                      ),
                      yandexStatusLine(it)
                        ? h(
                            "div",
                            {
                              style: {
                                marginTop: 4,
                                fontSize: 12,
                                fontWeight: 600,
                                color: String(it?.yandex_status || it?.yandex_raw?.status || "").toLowerCase() === "in_progress" ? "#b45309" : "#64748b",
                              },
                            },
                            yandexStatusLine(it)
                          )
                        : null,
                      it?.yandex_deduct_confirmed === false && String(it?.status || "").toLowerCase() !== "paid"
                        ? h(
                            "div",
                            { style: { marginTop: 2, fontSize: 12, color: "#b91c1c", fontWeight: 700 } },
                            "Düşüm onaylanmadı — IBAN/iade yok"
                          )
                        : null,
                      h(
                        "div",
                        { className: "muted", style: { marginTop: 4 } },
                        `Tutar: ${it.amount || 0}₺ • Hizmet Bedeli: ${it.fee || 0}₺ • Ödenecek: ${
                          it.payout_amount ?? it.net_amount ?? Math.max(0, Number(it.amount || 0) - Number(it.fee || 0))
                        }₺`
                      )
                    );
                  })
                : h("div", { className: "muted", style: { padding: 12 } }, "Kayıt yok.")
            ),
            selectedWithdrawalId
              ? h(
                  "div",
                  { style: { marginTop: 14, borderTop: "1px solid #eee", paddingTop: 12 } },
                  h("div", { style: { display: "grid", gap: 8 } }, [
                    h("label", { className: "label" }, "Durum"),
                    h(
                      "select",
                      { className: "input", value: wdStatus, onChange: (e) => setWdStatus(e.target.value) },
                      h("option", { value: "payout_pending" }, "IBAN banka onayı bekleniyor"),
                      h("option", { value: "payout_failed" }, "IBAN gönderimi başarısız"),
                      h("option", { value: "refunded" }, "Yandex iade edildi"),
                      h("option", { value: "paid" }, "Ödendi (manuel)"),
                      h("option", { value: "rejected" }, "Reddet"),
                      h("option", { value: "pending" }, "Beklemede")
                    ),
                    h("label", { className: "label" }, "Not"),
                    h("textarea", {
                      className: "input",
                      style: { minHeight: 80, paddingTop: 10 },
                      value: wdNote,
                      onChange: (e) => setWdNote(e.target.value),
                    }),
                    h(
                      "button",
                      { className: "btn", type: "button", disabled: busy, onClick: saveWithdrawal, style: { marginTop: 6 } },
                      busy
                        ? "İşleniyor..."
                        : wdStatus === "rejected"
                        ? "Reddet"
                        : wdStatus === "paid"
                        ? "Ödendi Olarak Kaydet"
                        : "Kaydet"
                    ),
                  ])
                )
              : null
          )
        : null,

      section === "vakifbank_ty_payments" ? h(AdminVakifbankTyPaymentsPage) : null,

      // ANNOUNCEMENTS
      section === "announcements"
        ? h(
            React.Fragment,
            null,

            // LIST
            view === "list"
              ? h(
                  "div",
                  { className: "card info-card", style: { marginTop: 14 } },
                  h(
                    "div",
                    { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 } },
                    h("div", { className: "card-title", style: { fontWeight: 900, fontSize: 18 } }, "Duyurular"),
                    h("button", { className: "btn", type: "button", onClick: openCreate }, "+ Yeni")
                  ),

                  h(
                    "div",
                    { style: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10, alignItems: "center" } },
                    h(
                      "select",
                      { className: "input", style: { maxWidth: 240 }, value: filter, onChange: (e) => setFilter(e.target.value) },
                      h("option", { value: "all" }, "Filtre: Tümü"),
                      h("option", { value: "active" }, "Aktif"),
                      h("option", { value: "pinned" }, "Sabit (Pinned)"),
                      h("option", { value: "deleted" }, "Silinmiş")
                    ),
                    h("div", { className: "muted" }, busy ? "İşlem yapılıyor..." : "")
                  ),

                  filteredItems && filteredItems.length
                    ? h(
                        "div",
                        { style: { marginTop: 10 } },
                        filteredItems.map((it) =>
                          h(
                            "div",
                            {
                              key: it.id,
                              style: { borderTop: "1px solid #eee", padding: "12px 0", opacity: it.is_deleted ? 0.55 : 1, cursor: "pointer" },
                              onClick: () => {
                                setSelectedId(it.id);
                                setView("detail");
                              },
                            },
                            h(
                              "div",
                              { style: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 } },
                              h(
                                "div",
                                { style: { display: "flex", flexDirection: "column", gap: 6 } },
                                h("div", { style: { fontWeight: 900 } }, it.title || "(başlıksız)"),
                                h("div", { style: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" } }, [
                                  h(StatusPill, { item: it }),
                                  h("span", { className: "muted" }, safeDateLabel(it.created_at)),
                                ])
                              ),
                              h(
                                "div",
                                { style: { display: "flex", gap: 8, alignItems: "center" } },
                                h(IconBtn, {
                                  title: it.pinned ? "Unpin" : "Pin",
                                  disabled: busy || it.is_deleted,
                                  onClick: (e) => {
                                    e.stopPropagation();
                                    togglePinned(it);
                                  },
                                  children: it.pinned ? "📌" : "📍",
                                }),
                                h(IconBtn, {
                                  title: "Düzenle",
                                  disabled: busy || it.is_deleted,
                                  onClick: (e) => {
                                    e.stopPropagation();
                                    openEditFromItem(it);
                                  },
                                  children: "✏️",
                                }),
                                h(IconBtn, {
                                  title: "Sil",
                                  disabled: busy || it.is_deleted,
                                  onClick: (e) => {
                                    e.stopPropagation();
                                    softDelete(it.id);
                                  },
                                  children: "🗑️",
                                })
                              )
                            ),
                            h("div", { className: "muted", style: { marginTop: 8 } }, "İller: ", parksText(it.parks))
                          )
                        )
                      )
                    : h("div", { className: "muted", style: { marginTop: 12 } }, "Henüz duyuru yok.")
                )
              : null,

            // CREATE / EDIT
            view === "create"
              ? h(
                  "div",
                  { className: "card info-card", style: { marginTop: 14 } },
                  h(
                    "div",
                    { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 } },
                    h("div", { className: "card-title", style: { fontWeight: 900, fontSize: 18 } }, editId ? "Duyuru Düzenle" : "Yeni Duyuru"),
                    h(
                      "button",
                      {
                        className: "btn-outline",
                        type: "button",
                        onClick: () => {
                          setView("list");
                          resetForm();
                        },
                      },
                      "Geri"
                    )
                  ),

                  h(
                    "form",
                    { className: "form", onSubmit: submitForm },

                    h("label", { className: "label" }, "Başlık"),
                    h("input", { className: "input", value: title, onChange: (e) => setTitle(e.target.value), placeholder: "Örn: Kampanya duyurusu" }),

                    h("label", { className: "label", style: { marginTop: 10 } }, "Metin"),
                    h("textarea", {
                      className: "input",
                      style: { minHeight: 120, paddingTop: 10 },
                      value: body,
                      onChange: (e) => setBody(e.target.value),
                      placeholder: "Duyuru metni...",
                    }),

                    h("div", { style: { display: "flex", gap: 14, alignItems: "center", marginTop: 10, flexWrap: "wrap" } },
                      h("label", { style: { display: "inline-flex", gap: 10, alignItems: "center", cursor: "pointer" } }, [
                        h("input", { type: "checkbox", checked: pinned, onChange: (e) => setPinned(e.target.checked) }),
                        h("span", null, "Sabit (pinned)"),
                      ])
                    ),

                    h("div", { style: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 } },
                      h("div", { style: { flex: "1 1 220px" } },
                        h("label", { className: "label" }, "Başlangıç (opsiyonel)"),
                        h("input", { className: "input", type: "datetime-local", value: startAt, onChange: (e) => setStartAt(e.target.value) })
                      ),
                      h("div", { style: { flex: "1 1 220px" } },
                        h("label", { className: "label" }, "Bitiş (opsiyonel)"),
                        h("input", { className: "input", type: "datetime-local", value: endAt, onChange: (e) => setEndAt(e.target.value) })
                      )
                    ),

                    h("div", { className: "muted", style: { marginTop: 10 } }, "Hangi illerde görünsün?"),

                    h("div", { style: { marginTop: 8, display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" } },
                      h("label", { style: { display: "inline-flex", gap: 10, alignItems: "center", cursor: "pointer" } }, [
                        h("input", {
                          type: "checkbox",
                          checked: allParks,
                          onChange: (e) => {
                            const v = !!e.target.checked;
                            setAllParks(v);
                            if (v) setSelectedParks([]);
                          },
                        }),
                        h("span", { style: { fontWeight: 700 } }, "Tümü (*)"),
                      ]),
                      h("div", { style: { flex: "1 1 320px", minWidth: 260, maxWidth: 520 } },
                        h(
                          "select",
                          {
                            className: "input",
                            multiple: true,
                            size: 7,
                            disabled: allParks || busy,
                            value: selectedParks,
                            onChange: (e) => {
                              const opts = Array.from(e.target.options);
                              const chosen = opts.filter((o) => o.selected).map((o) => o.value);
                              setSelectedParks(chosen);
                            },
                            style: { height: 190, paddingTop: 10, background: "#fff", color: "#111", border: "1px solid #e7e7e7" },
                          },
                          parkOptions && parkOptions.length
                            ? parkOptions.map((p) => h("option", { key: p.key, value: p.key }, p.label))
                            : h("option", { value: "", disabled: true }, "İller yükleniyor...")
                        )
                      )
                    ),

                    h("div", { className: "muted", style: { marginTop: 8 } }, "Seçili: ", selectedParksText),

                    h("button", { className: "btn", disabled: busy, type: "submit", style: { marginTop: 12 } },
                      busy ? "Kaydediliyor..." : (editId ? "Güncelle" : "Kaydet")
                    )
                  )
                )
              : null,

            // DETAIL
            view === "detail" && selectedItem
              ? h(
                  "div",
                  { className: "card info-card", style: { marginTop: 14 } },
                  h(
                    "div",
                    { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 } },
                    h("div", { className: "card-title", style: { fontWeight: 900, fontSize: 18 } }, "Duyuru Detayı"),
                    h("button", { className: "btn-outline", type: "button", onClick: () => { setView("list"); setSelectedId(""); } }, "Geri")
                  ),

                  h(
                    "div",
                    { style: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginTop: 10 } },
                    h("div", { style: { display: "flex", flexDirection: "column", gap: 8 } },
                      h("div", { style: { fontWeight: 900, fontSize: 18 } }, selectedItem.title || "-"),
                      h(StatusPill, { item: selectedItem })
                    ),
                    h("div", { style: { display: "flex", gap: 8, alignItems: "center" } },
                      h(IconBtn, { title: selectedItem.pinned ? "Unpin" : "Pin", disabled: busy || selectedItem.is_deleted, onClick: () => togglePinned(selectedItem), children: selectedItem.pinned ? "📌" : "📍" }),
                      h(IconBtn, { title: "Düzenle", disabled: busy || selectedItem.is_deleted, onClick: () => openEditFromItem(selectedItem), children: "✏️" }),
                      h(IconBtn, { title: "Sil", disabled: busy || selectedItem.is_deleted, onClick: () => softDelete(selectedItem.id), children: "🗑️" })
                    )
                  ),

                  h("div", { className: "muted", style: { marginTop: 10 } }, "Tarih: ", safeDateLabel(selectedItem.created_at)),
                  h("div", { className: "muted", style: { marginTop: 6 } }, "İller: ", parksText(selectedItem.parks)),
                  h("div", { className: "muted", style: { marginTop: 6 } }, "Başlangıç: ", selectedItem.start_at ? safeDateLabel(selectedItem.start_at) : "-", " | Bitiş: ", selectedItem.end_at ? safeDateLabel(selectedItem.end_at) : "-"),
                  h("div", { style: { marginTop: 12, whiteSpace: "pre-wrap" } }, String(selectedItem.body || ""))
                )
              : null
          )
        : null
    )
  );
}
